import { Fragment, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Chip,
  Tabs,
  Tab,
  Tooltip,
  Typography,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  Collapse,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import { ArrowBack, ExpandLess, ExpandMore } from "@mui/icons-material";

import Page from "./Page";
import useProject from "../../hooks/useProject";
import useData from "../../hooks/useData";
import StorageLocations from "../inventory/StorageLocations";
import Dialog_YesNo from "../ui/Dialog_YesNo";
import Dialog_Flex from "../ui/Dialog_Flex";

const toDisplayText = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }

  if (typeof value === "object") {
    if (value?.buffer && typeof value.buffer === "object") {
      const byteValues = Object.values(value.buffer)
        .map((item) => Number(item))
        .filter((item) => Number.isInteger(item) && item >= 0 && item <= 255);

      if (byteValues.length > 0) {
        return byteValues
          .map((byte) => byte.toString(16).padStart(2, "0"))
          .join("");
      }
    }

    if (Array.isArray(value?.data)) {
      const byteValues = value.data
        .map((item) => Number(item))
        .filter((item) => Number.isInteger(item) && item >= 0 && item <= 255);

      if (byteValues.length > 0) {
        return byteValues
          .map((byte) => byte.toString(16).padStart(2, "0"))
          .join("");
      }
    }

    if (typeof value.$numberDecimal === "string") {
      return value.$numberDecimal;
    }

    if (typeof value.toString === "function") {
      const text = value.toString();
      if (text && text !== "[object Object]") {
        return text;
      }
    }

    try {
      return JSON.stringify(value);
    } catch (err) {
      return fallback;
    }
  }

  return String(value);
};

const toYyyyMmDd = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return toDisplayText(value, fallback);
  }

  return parsed.toISOString().slice(0, 10);
};

const getOrderRepLabel = (order) => {
  const orderedByUser = Array.isArray(order?.orderedByUser)
    ? order.orderedByUser[0]
    : null;
  if (!orderedByUser) return "";

  const firstName = toDisplayText(orderedByUser?.firstName, "").trim();
  const lastName = toDisplayText(orderedByUser?.lastName, "").trim();
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) return fullName;

  return toDisplayText(orderedByUser?.email, "").trim();
};

const getOrderLockerLabel = (order) => {
  const storageLocation = Array.isArray(order?.storageLocation)
    ? order.storageLocation[0]
    : null;
  const lockerName = toDisplayText(storageLocation?.name, "").trim();
  if (lockerName) return lockerName;

  return toDisplayText(order?.storageLocationId, "").trim();
};

const getSkuSapNumber = (sku) => {
  const fields = Array.isArray(sku?.fields) ? sku.fields : [];
  const sapField = fields.find((entry) => {
    const fieldName = toDisplayText(entry?.name, "").trim().toLowerCase();
    return ["knn", "sap", "sap#", "sap #"].includes(fieldName);
  });

  return toDisplayText(sapField?.value, "").trim();
};

const getReturnProcessingChipProps = (status) => {
  const key = String(status || "")
    .trim()
    .toLowerCase();

  if (key === "processed") {
    return { label: "Processed", color: "success", variant: "filled" };
  }

  if (key === "pickup requested") {
    return {
      label: "Pickup Requested",
      color: "warning",
      variant: "outlined",
    };
  }

  if (key === "picked up") {
    return { label: "Picked Up", color: "warning", variant: "filled" };
  }

  if (key === "closed") {
    return { label: "Closed", color: "default", variant: "outlined" };
  }

  return { label: "Draft", color: "default", variant: "outlined" };
};

const getReturnEmailChipProps = (status) => {
  const key = String(status || "")
    .trim()
    .toLowerCase();

  if (key === "sent") {
    return { label: "Sent", color: "success", variant: "filled" };
  }

  if (key === "failed") {
    return { label: "Failed", color: "error", variant: "filled" };
  }

  if (key === "pending") {
    return { label: "Pending", color: "warning", variant: "outlined" };
  }

  return { label: "Not Eligible", color: "default", variant: "outlined" };
};

const normalizeReturnType = (value) => {
  const key = String(value || "")
    .trim()
    .toLowerCase();
  if (key === "locker" || key === "positive") {
    return "locker";
  }
  return "hospital";
};

const getReturnTypeLabel = (value) =>
  normalizeReturnType(value) === "locker" ? "Locker" : "Hospital";

const Inventory = () => {
  const currentUserRole = useSelector((state) => state.authentication.role);
  const isSysAdmin = currentUserRole === "sysadmin";

  const { projectId } = useParams();
  const navigate = useNavigate();
  const { project, getProject } = useProject(projectId);
  const { getFromServer, executeAction } = useData();

  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [storageLocations, setStorageLocations] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [purchaseOrderExpanded, setPurchaseOrderExpanded] = useState({});
  const [sendingPoReceiptIds, setSendingPoReceiptIds] = useState({});
  const [resendConfirmReceipt, setResendConfirmReceipt] = useState(null);
  const [poReceiptSendStatus, setPoReceiptSendStatus] = useState("");
  const [poSkuFilter, setPoSkuFilter] = useState("");
  const [poRepFilter, setPoRepFilter] = useState("");
  const [poLockerFilter, setPoLockerFilter] = useState("");
  const [poProductCodeFilter, setPoProductCodeFilter] = useState("");
  const [poLotNumberFilter, setPoLotNumberFilter] = useState("");
  const [poDateFromFilter, setPoDateFromFilter] = useState("");
  const [poDateToFilter, setPoDateToFilter] = useState("");
  const [poStatusFilter, setPoStatusFilter] = useState("notReceived");
  const [salesOrders, setSalesOrders] = useState([]);
  const [salesOrderExpanded, setSalesOrderExpanded] = useState({});
  const [salesDateFrom, setSalesDateFrom] = useState("");
  const [salesDateTo, setSalesDateTo] = useState("");
  const [salesRepOptions, setSalesRepOptions] = useState([]);
  const [salesRepFilter, setSalesRepFilter] = useState("");
  const [salesSkuFilter, setSalesSkuFilter] = useState("");
  const [salesInventoryCodeFilter, setSalesInventoryCodeFilter] = useState("");
  const [salesProductCodeFilter, setSalesProductCodeFilter] = useState("");
  const [salesFiltersApplied, setSalesFiltersApplied] = useState(false);
  const [appliedSalesDateFrom, setAppliedSalesDateFrom] = useState("");
  const [appliedSalesDateTo, setAppliedSalesDateTo] = useState("");
  const [appliedSalesRepFilter, setAppliedSalesRepFilter] = useState("");
  const [appliedSalesSkuFilter, setAppliedSalesSkuFilter] = useState("");
  const [appliedSalesInventoryCodeFilter, setAppliedSalesInventoryCodeFilter] =
    useState("");
  const [appliedSalesProductCodeFilter, setAppliedSalesProductCodeFilter] =
    useState("");
  const [returnTransactions, setReturnTransactions] = useState([]);
  const [returnsDateFrom, setReturnsDateFrom] = useState("");
  const [returnsDateTo, setReturnsDateTo] = useState("");
  const [returnsRepFilter, setReturnsRepFilter] = useState("");
  const [returnsHospitalFilter, setReturnsHospitalFilter] = useState("");
  const [returnsSkuFilter, setReturnsSkuFilter] = useState("");
  const [returnsLotNumberFilter, setReturnsLotNumberFilter] = useState("");
  const [returnsTypeFilter, setReturnsTypeFilter] = useState("");
  const [returnsProcessedFilter, setReturnsProcessedFilter] = useState("");
  const [returnsEmailedFilter, setReturnsEmailedFilter] = useState("");
  const [returnsAppliedFilters, setReturnsAppliedFilters] = useState({
    dateFrom: "",
    dateTo: "",
    repId: "",
    hospitalId: "",
    skuId: "",
    lotNumber: "",
    returnType: "",
    processedStatus: "",
    emailStatus: "",
  });
  const [returnsHasRun, setReturnsHasRun] = useState(false);
  const [returnsQueryStatus, setReturnsQueryStatus] = useState("");
  const [returnsSlipStatus, setReturnsSlipStatus] = useState("");
  const [returnsMajorExpanded, setReturnsMajorExpanded] = useState({});
  const [returnsShipmentExpanded, setReturnsShipmentExpanded] = useState({});
  const [hospitalRows, setHospitalRows] = useState([]);
  const [adjustmentModalOpen, setAdjustmentModalOpen] = useState(false);
  const [selectedDeliveryLine, setSelectedDeliveryLine] = useState(null);
  const [adjustedQtyCasesInput, setAdjustedQtyCasesInput] = useState("");
  const [adjustmentReasonInput, setAdjustmentReasonInput] = useState("");
  const [adjustmentNotesInput, setAdjustmentNotesInput] = useState("");
  const [adjustmentStatus, setAdjustmentStatus] = useState("");
  const [positiveSnapshotLockers, setPositiveSnapshotLockers] = useState([]);
  const [positiveSnapshotExpanded, setPositiveSnapshotExpanded] = useState({});
  const [positiveSnapshotIncludeHistory, setPositiveSnapshotIncludeHistory] =
    useState(false);
  const [positiveSnapshotHistoryDateFrom, setPositiveSnapshotHistoryDateFrom] =
    useState("");
  const [positiveSnapshotHistoryDateTo, setPositiveSnapshotHistoryDateTo] =
    useState("");
  const [positiveSnapshotStatus, setPositiveSnapshotStatus] = useState("");
  const [positiveSnapshotSkuFilter, setPositiveSnapshotSkuFilter] =
    useState("");
  const [
    positiveSnapshotInventoryCodeFilter,
    setPositiveSnapshotInventoryCodeFilter,
  ] = useState("");
  const [
    positiveSnapshotProductCodeFilter,
    setPositiveSnapshotProductCodeFilter,
  ] = useState("");
  const [consumptionDateFrom, setConsumptionDateFrom] = useState("");
  const [consumptionDateTo, setConsumptionDateTo] = useState("");
  const [consumptionHospitalCodeFilter, setConsumptionHospitalCodeFilter] =
    useState("");
  const [consumptionRepFilter, setConsumptionRepFilter] = useState("");
  const [lotExpiryInventoryCodeFilter, setLotExpiryInventoryCodeFilter] =
    useState("");
  const [selectedReportType, setSelectedReportType] = useState("consumption");
  const [consumptionHasRun, setConsumptionHasRun] = useState(false);
  const [consumptionAppliedFilters, setConsumptionAppliedFilters] = useState({
    dateFrom: "",
    dateTo: "",
    locationId: "",
    rep: "",
  });
  const [lotExpiryHasRun, setLotExpiryHasRun] = useState(false);
  const [lotExpiryAppliedFilters, setLotExpiryAppliedFilters] = useState({
    dateFrom: "",
    dateTo: "",
    locationId: "",
    rep: "",
    inventoryCode: "",
  });
  const [hospitalLocationSummaryHasRun, setHospitalLocationSummaryHasRun] =
    useState(false);
  const [
    hospitalLocationSummaryAppliedFilters,
    setHospitalLocationSummaryAppliedFilters,
  ] = useState({
    dateFrom: "",
    dateTo: "",
    locationId: "",
    rep: "",
  });
  const [hospitalLocationDetailHasRun, setHospitalLocationDetailHasRun] =
    useState(false);
  const [
    hospitalLocationDetailAppliedFilters,
    setHospitalLocationDetailAppliedFilters,
  ] = useState({
    dateFrom: "",
    dateTo: "",
    locationId: "",
    rep: "",
  });
  const [consumptionPage, setConsumptionPage] = useState(0);
  const [consumptionRowsPerPage, setConsumptionRowsPerPage] = useState(25);
  const [positiveSettings, setPositiveSettings] = useState({
    credentials: {
      baseUrl: "",
      tokenUrl: "",
      inventoryPath: "",
      productsPath: "",
      audience: "",
      scope: "",
      customerCode: "",
      clientId: "",
      clientSecret: "",
    },
    token: {
      accessToken: "",
      tokenType: "Bearer",
      expiresInSeconds: 0,
      expiresAtUtc: "",
    },
    schedule: {
      timezone: "America/Toronto",
      runTime: "06:00",
      refreshBufferMinutes: 15,
    },
    latestRun: null,
  });
  const [positiveSettingsStatus, setPositiveSettingsStatus] = useState("");

  const positiveSnapshotSkuOptions = useMemo(() => {
    const map = new Map();
    positiveSnapshotLockers.forEach((locker) => {
      if (!Array.isArray(locker?.snapshotItems)) return;
      locker.snapshotItems.forEach((item) => {
        const optionKey = `${item?.skuId || ""}|${item?.skuName || ""}`;
        if (!map.has(optionKey)) {
          map.set(optionKey, {
            value: optionKey,
            label: item?.skuName || item?.skuId || "Unknown SKU",
          });
        }
      });
    });
    return [...map.values()].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );
  }, [positiveSnapshotLockers]);

  const purchaseOrderFilterOptions = useMemo(() => {
    const repSet = new Set();
    const lockerSet = new Set();
    const statusSet = new Set();

    purchaseOrders.forEach((order) => {
      const repLabel = getOrderRepLabel(order);
      if (repLabel) repSet.add(repLabel);

      const lockerLabel = getOrderLockerLabel(order);
      if (lockerLabel) lockerSet.add(lockerLabel);

      const status = toDisplayText(order?.status, "").trim().toLowerCase();
      if (status) statusSet.add(status);
    });

    return {
      repOptions: [...repSet].sort((a, b) => a.localeCompare(b)),
      lockerOptions: [...lockerSet].sort((a, b) => a.localeCompare(b)),
      statusOptions: [...statusSet].sort((a, b) => a.localeCompare(b)),
    };
  }, [purchaseOrders]);

  const filteredPurchaseOrders = useMemo(() => {
    const skuTerm = poSkuFilter.trim().toLowerCase();
    const productCodeTerm = poProductCodeFilter.trim().toLowerCase();
    const lotNumberTerm = poLotNumberFilter.trim().toLowerCase();
    const repTerm = poRepFilter.trim().toLowerCase();
    const lockerTerm = poLockerFilter.trim().toLowerCase();

    const fromTime = poDateFromFilter
      ? new Date(`${poDateFromFilter}T00:00:00`).getTime()
      : null;
    const toTime = poDateToFilter
      ? new Date(`${poDateToFilter}T23:59:59`).getTime()
      : null;

    return purchaseOrders.filter((order) => {
      const status = toDisplayText(order?.status, "").trim().toLowerCase();

      if (poStatusFilter === "notReceived" && status === "received") {
        return false;
      }

      if (
        poStatusFilter &&
        poStatusFilter !== "notReceived" &&
        status !== poStatusFilter.toLowerCase()
      ) {
        return false;
      }

      const repLabel = getOrderRepLabel(order).toLowerCase();
      if (repTerm && repLabel !== repTerm) {
        return false;
      }

      const lockerLabel = getOrderLockerLabel(order).toLowerCase();
      if (lockerTerm && lockerLabel !== lockerTerm) {
        return false;
      }

      const orderDateTime = order?.orderDate
        ? new Date(order.orderDate).getTime()
        : null;

      if (fromTime && (!orderDateTime || orderDateTime < fromTime)) {
        return false;
      }

      if (toTime && (!orderDateTime || orderDateTime > toTime)) {
        return false;
      }

      const skuDetails = Array.isArray(order?.skuDetails)
        ? order.skuDetails
        : [];

      if (skuTerm) {
        const hasSku = skuDetails.some((sku) =>
          String(sku?.name || "")
            .toLowerCase()
            .includes(skuTerm),
        );
        if (!hasSku) return false;
      }

      if (productCodeTerm) {
        const hasProductCode = skuDetails.some((sku) =>
          String(sku?.code || sku?.clientCode || "")
            .toLowerCase()
            .includes(productCodeTerm),
        );
        if (!hasProductCode) return false;
      }

      if (lotNumberTerm) {
        const receipts = Array.isArray(order?.receipts) ? order.receipts : [];
        const hasLotNumber = receipts.some((receipt) => {
          const lines = Array.isArray(receipt?.lines) ? receipt.lines : [];
          return lines.some((line) =>
            String(line?.lotNumber || "")
              .toLowerCase()
              .includes(lotNumberTerm),
          );
        });

        if (!hasLotNumber) return false;
      }

      return true;
    });
  }, [
    purchaseOrders,
    poSkuFilter,
    poProductCodeFilter,
    poLotNumberFilter,
    poRepFilter,
    poLockerFilter,
    poDateFromFilter,
    poDateToFilter,
    poStatusFilter,
  ]);

  const filteredPositiveSnapshotLockers = useMemo(() => {
    const inventoryCodeFilter = positiveSnapshotInventoryCodeFilter
      .trim()
      .toLowerCase();
    const productCodeFilter = positiveSnapshotProductCodeFilter
      .trim()
      .toLowerCase();

    return positiveSnapshotLockers
      .map((locker) => {
        const snapshotItems = Array.isArray(locker?.snapshotItems)
          ? locker.snapshotItems
          : [];

        const filteredItems = snapshotItems.filter((item) => {
          const itemSkuKey = `${item?.skuId || ""}|${item?.skuName || ""}`;
          const itemInventoryCode = String(
            item?.inventoryCode || "",
          ).toLowerCase();
          const itemProductCode = String(
            item?.productCode || item?.skuUpc || "",
          ).toLowerCase();

          if (
            positiveSnapshotSkuFilter &&
            itemSkuKey !== positiveSnapshotSkuFilter
          )
            return false;
          if (
            inventoryCodeFilter &&
            !itemInventoryCode.includes(inventoryCodeFilter)
          )
            return false;
          if (productCodeFilter && !itemProductCode.includes(productCodeFilter))
            return false;
          return true;
        });

        const sortedItems = [...filteredItems].sort((a, b) => {
          const aCode = String(a?.productCode || a?.skuUpc || "").toLowerCase();
          const bCode = String(b?.productCode || b?.skuUpc || "").toLowerCase();

          if (aCode !== bCode) {
            return aCode.localeCompare(bCode);
          }

          const aLot = String(a?.lotNumber || "").toLowerCase();
          const bLot = String(b?.lotNumber || "").toLowerCase();
          if (aLot !== bLot) {
            return aLot.localeCompare(bLot);
          }

          const aSnapshot = new Date(a?.snapshotAt || 0).getTime();
          const bSnapshot = new Date(b?.snapshotAt || 0).getTime();
          return bSnapshot - aSnapshot;
        });

        if (sortedItems.length === 0) return null;

        return {
          ...locker,
          snapshotItems: sortedItems,
          snapshotRowCount: sortedItems.length,
          totalOfficialQuantity: sortedItems.reduce(
            (sum, item) => sum + Number(item?.quantityOfficial || 0),
            0,
          ),
          snapshotAsOf: sortedItems.reduce((latest, item) => {
            const itemTime = new Date(item?.snapshotAt || 0).getTime();
            const latestTime = new Date(latest || 0).getTime();
            return itemTime > latestTime ? item.snapshotAt : latest;
          }, null),
        };
      })
      .filter(Boolean);
  }, [
    positiveSnapshotLockers,
    positiveSnapshotSkuFilter,
    positiveSnapshotInventoryCodeFilter,
    positiveSnapshotProductCodeFilter,
  ]);

  const salesSkuOptions = useMemo(() => {
    const map = new Map();
    salesOrders.forEach((order) => {
      if (!Array.isArray(order?.items)) return;
      order.items.forEach((item) => {
        const skuValue = item?.skuId || "";
        if (!skuValue || map.has(skuValue)) return;
        map.set(skuValue, {
          value: skuValue,
          label: item?.skuName || skuValue,
        });
      });
    });
    return [...map.values()].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );
  }, [salesOrders]);

  const filteredSalesDeliveries = useMemo(() => {
    if (!salesFiltersApplied) {
      return [];
    }

    const inventoryCodeFilter = appliedSalesInventoryCodeFilter
      .trim()
      .toLowerCase();
    const productCodeFilter = appliedSalesProductCodeFilter
      .trim()
      .toLowerCase();
    const fromTime = appliedSalesDateFrom
      ? new Date(`${appliedSalesDateFrom}T00:00:00`).getTime()
      : null;
    const toTime = appliedSalesDateTo
      ? new Date(`${appliedSalesDateTo}T23:59:59`).getTime()
      : null;

    return salesOrders
      .map((order) => {
        const deliveryDate =
          order?.deliveryDate || order?.visitDate || order?.orderDate;
        const deliveryTime = deliveryDate
          ? new Date(deliveryDate).getTime()
          : null;

        if (
          fromTime !== null &&
          (deliveryTime === null || deliveryTime < fromTime)
        ) {
          return null;
        }
        if (
          toTime !== null &&
          (deliveryTime === null || deliveryTime > toTime)
        ) {
          return null;
        }
        const deliveryUserId = String(order?.userId || "").trim();
        if (appliedSalesRepFilter && deliveryUserId !== appliedSalesRepFilter) {
          return null;
        }

        const items = Array.isArray(order?.items) ? order.items : [];
        const filteredItems = items.filter((item) => {
          const itemInventoryCode = String(
            item?.inventoryCode || item?.clientCode || "",
          ).toLowerCase();
          const itemProductCode = String(
            item?.productCode || item?.upc || "",
          ).toLowerCase();

          if (appliedSalesSkuFilter && item?.skuId !== appliedSalesSkuFilter) {
            return false;
          }
          if (
            inventoryCodeFilter &&
            !itemInventoryCode.includes(inventoryCodeFilter)
          ) {
            return false;
          }
          if (
            productCodeFilter &&
            !itemProductCode.includes(productCodeFilter)
          ) {
            return false;
          }
          return true;
        });

        if (filteredItems.length === 0) {
          return null;
        }

        return {
          ...order,
          deliveryDate,
          filteredItems,
          deliveryItemCount: filteredItems.length,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        const aTime = a?.deliveryDate ? new Date(a.deliveryDate).getTime() : 0;
        const bTime = b?.deliveryDate ? new Date(b.deliveryDate).getTime() : 0;
        return bTime - aTime;
      });
  }, [
    salesFiltersApplied,
    salesOrders,
    appliedSalesDateFrom,
    appliedSalesDateTo,
    appliedSalesRepFilter,
    appliedSalesSkuFilter,
    appliedSalesInventoryCodeFilter,
    appliedSalesProductCodeFilter,
  ]);

  const returnRepOptions = useMemo(() => {
    const map = new Map();

    returnTransactions.forEach((row) => {
      const repId = String(row?.user?.id || row?.userId || "").trim();
      if (!repId || map.has(repId)) return;

      const firstName = String(row?.user?.firstName || "").trim();
      const lastName = String(row?.user?.lastName || "").trim();
      const fullName = `${firstName} ${lastName}`.trim();
      const label =
        fullName ||
        String(row?.user?.email || row?.performedBy || row?.userId || repId);

      map.set(repId, {
        value: repId,
        label,
      });
    });

    return [...map.values()].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );
  }, [returnTransactions]);

  const returnHospitalOptions = useMemo(() => {
    const map = new Map();

    returnTransactions.forEach((row) => {
      const locationId = String(
        row?.hospitalLocation?.id || row?.hospitalLocationId || "",
      ).trim();
      if (!locationId || map.has(locationId)) return;

      const locationName = String(
        row?.hospitalLocation?.name || row?.hospitalLocationId || locationId,
      ).trim();
      const locationCode = String(
        row?.hospitalLocation?.code || row?.hospitalLocation?.clientCode || "",
      ).trim();

      map.set(locationId, {
        value: locationId,
        label: locationCode
          ? `${locationCode} - ${locationName || locationId}`
          : locationName || locationId,
      });
    });

    return [...map.values()].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );
  }, [returnTransactions]);

  const returnSkuOptions = useMemo(() => {
    const map = new Map();

    returnTransactions.forEach((row) => {
      const skuId = String(row?.sku?.id || row?.skuId || "").trim();
      if (!skuId || map.has(skuId)) return;

      const skuName = String(row?.sku?.name || row?.skuId || skuId).trim();
      const productCode = String(
        row?.sku?.code || row?.inventoryCode || "",
      ).trim();

      map.set(skuId, {
        value: skuId,
        label: productCode ? `${productCode} - ${skuName}` : skuName,
      });
    });

    return [...map.values()].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );
  }, [returnTransactions]);

  const filteredReturnTransactions = useMemo(() => {
    if (!returnsHasRun) {
      return [];
    }

    const normalizeStatus = (value) =>
      String(value || "")
        .trim()
        .toLowerCase();

    const lotNumberFilter = String(returnsAppliedFilters.lotNumber || "")
      .trim()
      .toLowerCase();
    const returnTypeFilter = normalizeReturnType(
      returnsAppliedFilters.returnType,
    );
    const processedStatusFilter = normalizeStatus(
      returnsAppliedFilters.processedStatus,
    );
    const emailStatusFilter = normalizeStatus(
      returnsAppliedFilters.emailStatus,
    );
    const fromTime = returnsAppliedFilters.dateFrom
      ? new Date(`${returnsAppliedFilters.dateFrom}T00:00:00`).getTime()
      : null;
    const toTime = returnsAppliedFilters.dateTo
      ? new Date(`${returnsAppliedFilters.dateTo}T23:59:59`).getTime()
      : null;

    return returnTransactions
      .filter((row) => {
        const transactionDateTime = row?.transactionDate
          ? new Date(row.transactionDate).getTime()
          : null;

        if (
          fromTime !== null &&
          (!transactionDateTime || transactionDateTime < fromTime)
        ) {
          return false;
        }

        if (
          toTime !== null &&
          (!transactionDateTime || transactionDateTime > toTime)
        ) {
          return false;
        }

        const repId = String(row?.user?.id || row?.userId || "").trim();
        if (
          returnsAppliedFilters.repId &&
          repId !== returnsAppliedFilters.repId
        ) {
          return false;
        }

        const hospitalId = String(
          row?.hospitalLocation?.id || row?.hospitalLocationId || "",
        ).trim();
        if (
          returnsAppliedFilters.hospitalId &&
          hospitalId !== returnsAppliedFilters.hospitalId
        ) {
          return false;
        }

        const skuId = String(row?.sku?.id || row?.skuId || "").trim();
        if (
          returnsAppliedFilters.skuId &&
          skuId !== returnsAppliedFilters.skuId
        ) {
          return false;
        }

        const lotNumber = String(row?.lotNumber || "").toLowerCase();
        if (lotNumberFilter && !lotNumber.includes(lotNumberFilter)) {
          return false;
        }

        if (returnsAppliedFilters.returnType) {
          const rowType = normalizeReturnType(row?.returnType);
          if (rowType !== returnTypeFilter) {
            return false;
          }
        }

        const rowProcessingStatus = normalizeStatus(
          row?.returnProcessingStatus,
        );
        if (
          processedStatusFilter &&
          rowProcessingStatus !== processedStatusFilter
        ) {
          return false;
        }

        const rowEmailStatus = normalizeStatus(row?.returnShipmentEmailStatus);
        if (emailStatusFilter && rowEmailStatus !== emailStatusFilter) {
          return false;
        }

        return true;
      })
      .sort((a, b) => {
        const aTime = a?.transactionDate
          ? new Date(a.transactionDate).getTime()
          : 0;
        const bTime = b?.transactionDate
          ? new Date(b.transactionDate).getTime()
          : 0;
        return bTime - aTime;
      });
  }, [returnsHasRun, returnTransactions, returnsAppliedFilters]);

  const groupedReturnTransactions = useMemo(() => {
    const repLockerMap = new Map();

    const getSortableTime = (value) => {
      if (!value) return 0;
      const parsed = new Date(value).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    for (const row of filteredReturnTransactions) {
      const repId =
        String(row?.user?.id || row?.userId || "").trim() || "unknown-rep";
      const repName = `${String(row?.user?.firstName || "").trim()} ${String(
        row?.user?.lastName || "",
      ).trim()}`.trim();
      const repLabel =
        repName ||
        String(
          row?.user?.email || row?.performedBy || row?.userId || "Unknown Rep",
        );

      const lockerId =
        String(row?.locker?.id || "").trim() || "unassigned-locker";
      const lockerName =
        String(row?.locker?.name || "").trim() || "Unassigned Locker";
      const lockerWarehouseCode = String(
        row?.locker?.warehouseCode || "",
      ).trim();
      const lockerAddress = String(row?.locker?.address || "").trim();
      const lockerLabel = lockerWarehouseCode
        ? `${lockerWarehouseCode} - ${lockerName}`
        : lockerName;

      const majorKey = `${repId}::${lockerId}`;
      if (!repLockerMap.has(majorKey)) {
        repLockerMap.set(majorKey, {
          key: majorKey,
          rep: {
            id: repId,
            label: repLabel,
          },
          locker: {
            id: lockerId,
            label: lockerLabel,
            name: lockerName,
            warehouseCode: lockerWarehouseCode,
            address: lockerAddress,
          },
          shipmentsMap: new Map(),
          totalRows: 0,
        });
      }

      const majorGroup = repLockerMap.get(majorKey);
      majorGroup.totalRows += 1;

      const shipmentId = String(
        row?.returnHeaderProcessedShipmentId || "",
      ).trim();
      const shipmentKey = shipmentId || "__not_processed__";

      if (!majorGroup.shipmentsMap.has(shipmentKey)) {
        majorGroup.shipmentsMap.set(shipmentKey, {
          key: shipmentKey,
          shipmentId: shipmentId || null,
          isProcessed: !!shipmentId,
          rows: [],
          maxProcessedAt: 0,
          maxTransactionAt: 0,
        });
      }

      const shipment = majorGroup.shipmentsMap.get(shipmentKey);
      shipment.rows.push(row);
      shipment.maxProcessedAt = Math.max(
        shipment.maxProcessedAt,
        getSortableTime(row?.returnHeaderProcessedAt),
      );
      shipment.maxTransactionAt = Math.max(
        shipment.maxTransactionAt,
        getSortableTime(row?.transactionDate),
      );
    }

    const majorGroups = Array.from(repLockerMap.values()).map((group) => {
      const shipments = Array.from(group.shipmentsMap.values())
        .map((shipment) => ({
          ...shipment,
          rows: shipment.rows.sort((a, b) => {
            const left = getSortableTime(a?.transactionDate);
            const right = getSortableTime(b?.transactionDate);
            return right - left;
          }),
        }))
        .sort((a, b) => {
          if (a.isProcessed !== b.isProcessed) {
            return a.isProcessed ? -1 : 1;
          }

          if (a.isProcessed && b.isProcessed) {
            if (b.maxProcessedAt !== a.maxProcessedAt) {
              return b.maxProcessedAt - a.maxProcessedAt;
            }
            return String(a.shipmentId || "").localeCompare(
              String(b.shipmentId || ""),
            );
          }

          return b.maxTransactionAt - a.maxTransactionAt;
        });

      return {
        ...group,
        shipments,
      };
    });

    return majorGroups.sort((a, b) => {
      const repSort = String(a.rep.label || "").localeCompare(
        String(b.rep.label || ""),
        undefined,
        {
          sensitivity: "base",
        },
      );
      if (repSort !== 0) return repSort;
      return String(a.locker.label || "").localeCompare(
        String(b.locker.label || ""),
        undefined,
        {
          sensitivity: "base",
        },
      );
    });
  }, [filteredReturnTransactions]);

  const toggleReturnsMajorGroup = (majorKey) => {
    setReturnsMajorExpanded((current) => ({
      ...current,
      [majorKey]: !current[majorKey],
    }));
  };

  const toggleReturnsShipmentGroup = (majorKey, shipmentKey) => {
    const key = `${majorKey}::${shipmentKey}`;
    setReturnsShipmentExpanded((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const consumptionRepOptions = useMemo(() => {
    const map = new Map();
    salesOrders.forEach((delivery) => {
      const repValue = String(
        delivery?.repName || delivery?.userId || "",
      ).trim();
      if (!repValue || map.has(repValue)) return;
      map.set(repValue, repValue);
    });
    return [...map.values()].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
  }, [salesOrders]);

  const consumptionHospitalOptions = useMemo(() => {
    const map = new Map();
    hospitalRows.forEach((row) => {
      const locationId = String(row?.locationId || row?.id || "").trim();
      if (!locationId || map.has(locationId)) return;
      const clientCode = String(row?.clientCode || "").trim();
      const locationName =
        String(row?.name || row?.locationName || "Unknown Location").trim() ||
        "Unknown Location";
      map.set(locationId, {
        value: locationId,
        label: clientCode ? `${clientCode} - ${locationName}` : locationName,
      });
    });
    return [...map.values()].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );
  }, [hospitalRows]);

  const hasConsumptionDateRangeInput =
    !!consumptionDateFrom && !!consumptionDateTo;

  const consumptionRows = useMemo(() => {
    if (!consumptionHasRun) {
      return [];
    }

    const fromTime = new Date(
      `${consumptionAppliedFilters.dateFrom}T00:00:00`,
    ).getTime();
    const toTime = new Date(
      `${consumptionAppliedFilters.dateTo}T23:59:59`,
    ).getTime();

    if (Number.isNaN(fromTime) || Number.isNaN(toTime) || fromTime > toTime) {
      return [];
    }

    const locationById = new Map();
    hospitalRows.forEach((row) => {
      const key = String(row?.locationId || row?.id || "").trim();
      if (!key) return;
      locationById.set(key, row);
    });

    const grouped = new Map();

    salesOrders.forEach((delivery) => {
      const deliveryDateRaw = delivery?.deliveryDate;
      const deliveryTime = deliveryDateRaw
        ? new Date(deliveryDateRaw).getTime()
        : Number.NaN;

      if (
        Number.isNaN(deliveryTime) ||
        deliveryTime < fromTime ||
        deliveryTime > toTime
      ) {
        return;
      }

      const locationId = String(delivery?.locationId || "").trim();
      const repName = String(
        delivery?.repName || delivery?.userId || "",
      ).trim();
      const location = locationById.get(locationId) || {};
      const hospitalCode = String(location?.clientCode || "").trim();

      if (
        consumptionAppliedFilters.locationId &&
        locationId !== consumptionAppliedFilters.locationId
      ) {
        return;
      }

      if (
        consumptionAppliedFilters.rep &&
        repName !== consumptionAppliedFilters.rep
      ) {
        return;
      }

      const items = Array.isArray(delivery?.items) ? delivery.items : [];

      items.forEach((item) => {
        const deliveredUnits = Number(
          item?.effectiveQtyCases ?? item?.enteredQtyCases ?? 0,
        );
        if (!Number.isFinite(deliveredUnits) || deliveredUnits <= 0) {
          return;
        }

        const productCode = String(
          item?.productCode || item?.upc || item?.clientCode || "",
        ).trim();
        const skuName = String(item?.skuName || item?.name || "").trim();

        const groupKey = [
          locationId,
          hospitalCode,
          repName,
          productCode,
          skuName,
        ].join("|");

        const existing = grouped.get(groupKey);

        const baseRow = {
          hospitalCode: hospitalCode || "-",
          locationName:
            String(
              location?.name ||
                location?.locationName ||
                delivery?.locationName ||
                "-",
            ).trim() || "-",
          streetAddress: String(location?.streetAddress || "").trim() || "-",
          city: String(location?.city || "").trim() || "-",
          province: String(location?.province || "").trim() || "-",
          postal: String(location?.postal || "").trim() || "-",
          repName: repName || "-",
          productCode: productCode || "-",
          skuName: skuName || toDisplayText(item?.skuId),
          deliveredUnits: 0,
        };

        if (existing) {
          existing.deliveredUnits += deliveredUnits;
        } else {
          baseRow.deliveredUnits = deliveredUnits;
          grouped.set(groupKey, baseRow);
        }
      });
    });

    return [...grouped.values()].sort((a, b) => {
      const codeCompare = a.hospitalCode.localeCompare(b.hospitalCode);
      if (codeCompare !== 0) return codeCompare;
      const locationCompare = a.locationName.localeCompare(b.locationName);
      if (locationCompare !== 0) return locationCompare;
      const repCompare = a.repName.localeCompare(b.repName);
      if (repCompare !== 0) return repCompare;
      const productCompare = a.productCode.localeCompare(b.productCode);
      if (productCompare !== 0) return productCompare;
      return a.skuName.localeCompare(b.skuName);
    });
  }, [consumptionHasRun, consumptionAppliedFilters, hospitalRows, salesOrders]);

  const lotExpiryRows = useMemo(() => {
    if (!lotExpiryHasRun) {
      return [];
    }

    const fromTime = new Date(
      `${lotExpiryAppliedFilters.dateFrom}T00:00:00`,
    ).getTime();
    const toTime = new Date(
      `${lotExpiryAppliedFilters.dateTo}T23:59:59`,
    ).getTime();

    if (Number.isNaN(fromTime) || Number.isNaN(toTime) || fromTime > toTime) {
      return [];
    }

    const locationById = new Map();
    hospitalRows.forEach((row) => {
      const key = String(row?.locationId || row?.id || "").trim();
      if (!key) return;
      locationById.set(key, row);
    });

    const rows = [];

    salesOrders.forEach((delivery) => {
      const deliveryDateRaw = delivery?.deliveryDate;
      const deliveryTime = deliveryDateRaw
        ? new Date(deliveryDateRaw).getTime()
        : Number.NaN;
      const deliveryDate = !Number.isNaN(deliveryTime)
        ? new Date(deliveryDateRaw).toISOString().slice(0, 10)
        : "";

      if (
        Number.isNaN(deliveryTime) ||
        deliveryTime < fromTime ||
        deliveryTime > toTime
      ) {
        return;
      }

      const locationId = String(delivery?.locationId || "").trim();
      const repName = String(
        delivery?.repName || delivery?.userId || "",
      ).trim();
      const location = locationById.get(locationId) || {};
      const hospitalCode = String(location?.clientCode || "").trim();

      if (
        lotExpiryAppliedFilters.locationId &&
        locationId !== lotExpiryAppliedFilters.locationId
      ) {
        return;
      }

      if (
        lotExpiryAppliedFilters.rep &&
        repName !== lotExpiryAppliedFilters.rep
      ) {
        return;
      }

      const items = Array.isArray(delivery?.items) ? delivery.items : [];
      const inventoryCodeFilter = String(
        lotExpiryAppliedFilters.inventoryCode || "",
      )
        .trim()
        .toLowerCase();

      items.forEach((item) => {
        const deliveredUnits = Number(
          item?.effectiveQtyCases ?? item?.enteredQtyCases ?? 0,
        );
        if (!Number.isFinite(deliveredUnits) || deliveredUnits <= 0) {
          return;
        }

        const productCode = String(
          item?.productCode || item?.upc || item?.clientCode || "",
        ).trim();
        const skuName = String(item?.skuName || item?.name || "").trim();
        const inventoryCode = String(
          item?.inventoryCode || item?.clientCode || "",
        ).trim();
        if (
          inventoryCodeFilter &&
          !inventoryCode.toLowerCase().includes(inventoryCodeFilter)
        ) {
          return;
        }
        const lotNumber = String(item?.lotNumber || "").trim();
        const expiryDate = item?.expiryDate
          ? new Date(item.expiryDate).toISOString().slice(0, 10)
          : "";

        rows.push({
          referenceNumber: String(item?.lineId || item?.localId || "").trim(),
          hospitalCode: hospitalCode || "-",
          locationName:
            String(
              location?.name ||
                location?.locationName ||
                delivery?.locationName ||
                "-",
            ).trim() || "-",
          streetAddress: String(location?.streetAddress || "").trim() || "-",
          city: String(location?.city || "").trim() || "-",
          province: String(location?.province || "").trim() || "-",
          postal: String(location?.postal || "").trim() || "-",
          repName: repName || "-",
          deliveryDate: deliveryDate || "-",
          productCode: productCode || "-",
          skuName: skuName || toDisplayText(item?.skuId),
          inventoryCode: inventoryCode || "-",
          lotNumber: lotNumber || "-",
          expiryDate: expiryDate || "-",
          deliveredUnits,
        });
      });
    });

    return rows.sort((a, b) => {
      const codeCompare = a.hospitalCode.localeCompare(b.hospitalCode);
      if (codeCompare !== 0) return codeCompare;
      const locationCompare = a.locationName.localeCompare(b.locationName);
      if (locationCompare !== 0) return locationCompare;
      const deliveryDateCompare = a.deliveryDate.localeCompare(b.deliveryDate);
      if (deliveryDateCompare !== 0) return deliveryDateCompare;
      const productCompare = a.productCode.localeCompare(b.productCode);
      if (productCompare !== 0) return productCompare;
      const inventoryCompare = a.inventoryCode.localeCompare(b.inventoryCode);
      if (inventoryCompare !== 0) return inventoryCompare;
      const lotCompare = a.lotNumber.localeCompare(b.lotNumber);
      if (lotCompare !== 0) return lotCompare;
      const expiryCompare = a.expiryDate.localeCompare(b.expiryDate);
      if (expiryCompare !== 0) return expiryCompare;
      return String(a.referenceNumber || "").localeCompare(
        String(b.referenceNumber || ""),
      );
    });
  }, [lotExpiryHasRun, lotExpiryAppliedFilters, hospitalRows, salesOrders]);

  const hospitalLocationSummaryRows = useMemo(() => {
    if (!hospitalLocationSummaryHasRun) {
      return [];
    }

    const fromTime = new Date(
      `${hospitalLocationSummaryAppliedFilters.dateFrom}T00:00:00`,
    ).getTime();
    const toTime = new Date(
      `${hospitalLocationSummaryAppliedFilters.dateTo}T23:59:59`,
    ).getTime();

    if (Number.isNaN(fromTime) || Number.isNaN(toTime) || fromTime > toTime) {
      return [];
    }

    const locationById = new Map();
    hospitalRows.forEach((row) => {
      const key = String(row?.locationId || row?.id || "").trim();
      if (!key) return;
      locationById.set(key, row);
    });

    const grouped = new Map();
    const resolvePositiveQty = (...values) => {
      for (const value of values) {
        const qty = Number(value);
        if (Number.isFinite(qty) && qty > 0) {
          return qty;
        }
      }
      return 0;
    };

    salesOrders.forEach((delivery) => {
      const deliveryDateRaw = delivery?.deliveryDate;
      const deliveryTime = deliveryDateRaw
        ? new Date(deliveryDateRaw).getTime()
        : Number.NaN;

      if (
        Number.isNaN(deliveryTime) ||
        deliveryTime < fromTime ||
        deliveryTime > toTime
      ) {
        return;
      }

      const locationId = String(delivery?.locationId || "").trim();
      const repName = String(
        delivery?.repName || delivery?.userId || "",
      ).trim();
      const location = locationById.get(locationId) || {};
      const hospitalCode = String(location?.clientCode || "").trim();
      const hospitalName =
        String(
          location?.name ||
            location?.locationName ||
            delivery?.locationName ||
            "",
        ).trim() || "-";

      if (
        hospitalLocationSummaryAppliedFilters.locationId &&
        locationId !== hospitalLocationSummaryAppliedFilters.locationId
      ) {
        return;
      }

      if (
        hospitalLocationSummaryAppliedFilters.rep &&
        repName !== hospitalLocationSummaryAppliedFilters.rep
      ) {
        return;
      }

      const items = Array.isArray(delivery?.items) ? delivery.items : [];

      items.forEach((item) => {
        const allocations =
          Array.isArray(item?.allocations) && item.allocations.length > 0
            ? item.allocations
            : [
                {
                  name: "Unspecified Location",
                  enteredQtyEach: resolvePositiveQty(
                    item?.effectiveQtyCases,
                    item?.enteredQtyCases,
                    item?.normalizedQtyUnits,
                  ),
                  normalizedQty: resolvePositiveQty(
                    item?.normalizedQtyUnits,
                    item?.effectiveQtyCases,
                    item?.enteredQtyCases,
                  ),
                  productCode: item?.productCode || null,
                },
              ];

        allocations.forEach((allocation) => {
          const deliveredUnits = resolvePositiveQty(
            allocation?.normalizedQty,
            allocation?.enteredQtyEach,
            item?.effectiveQtyCases,
            item?.enteredQtyCases,
          );
          if (!Number.isFinite(deliveredUnits) || deliveredUnits <= 0) {
            return;
          }

          const hospitalLocationName =
            String(allocation?.name || "").trim() || "Unspecified Location";
          const productCode = String(
            allocation?.productCode ||
              item?.productCode ||
              item?.upc ||
              item?.clientCode ||
              "",
          ).trim();
          const skuName = String(item?.skuName || item?.name || "").trim();

          const groupKey = [
            repName,
            hospitalCode,
            hospitalName,
            hospitalLocationName,
            productCode,
            skuName,
          ].join("|");

          const existing = grouped.get(groupKey);
          if (existing) {
            existing.deliveredUnits += deliveredUnits;
          } else {
            grouped.set(groupKey, {
              repName: repName || "-",
              hospitalCode: hospitalCode || "-",
              hospitalName,
              hospitalLocationName,
              productCode: productCode || "-",
              skuName: skuName || toDisplayText(item?.skuId),
              deliveredUnits,
            });
          }
        });
      });
    });

    return [...grouped.values()].sort((a, b) => {
      const repCompare = a.repName.localeCompare(b.repName);
      if (repCompare !== 0) return repCompare;
      const hospitalCompare = a.hospitalCode.localeCompare(b.hospitalCode);
      if (hospitalCompare !== 0) return hospitalCompare;
      const skuCompare = a.skuName.localeCompare(b.skuName);
      if (skuCompare !== 0) return skuCompare;
      const locationCompare = a.hospitalLocationName.localeCompare(
        b.hospitalLocationName,
      );
      if (locationCompare !== 0) return locationCompare;
      const productCompare = a.productCode.localeCompare(b.productCode);
      if (productCompare !== 0) return productCompare;
      return a.hospitalName.localeCompare(b.hospitalName);
    });
  }, [
    hospitalLocationSummaryHasRun,
    hospitalLocationSummaryAppliedFilters,
    hospitalRows,
    salesOrders,
  ]);

  const hospitalLocationDetailRows = useMemo(() => {
    if (!hospitalLocationDetailHasRun) {
      return [];
    }

    const fromTime = new Date(
      `${hospitalLocationDetailAppliedFilters.dateFrom}T00:00:00`,
    ).getTime();
    const toTime = new Date(
      `${hospitalLocationDetailAppliedFilters.dateTo}T23:59:59`,
    ).getTime();

    if (Number.isNaN(fromTime) || Number.isNaN(toTime) || fromTime > toTime) {
      return [];
    }

    const locationById = new Map();
    hospitalRows.forEach((row) => {
      const key = String(row?.locationId || row?.id || "").trim();
      if (!key) return;
      locationById.set(key, row);
    });

    const rows = [];
    const resolvePositiveQty = (...values) => {
      for (const value of values) {
        const qty = Number(value);
        if (Number.isFinite(qty) && qty > 0) {
          return qty;
        }
      }
      return 0;
    };

    salesOrders.forEach((delivery) => {
      const deliveryDateRaw = delivery?.deliveryDate;
      const deliveryTime = deliveryDateRaw
        ? new Date(deliveryDateRaw).getTime()
        : Number.NaN;
      const deliveryDate = !Number.isNaN(deliveryTime)
        ? new Date(deliveryDateRaw).toISOString().slice(0, 10)
        : "";

      if (
        Number.isNaN(deliveryTime) ||
        deliveryTime < fromTime ||
        deliveryTime > toTime
      ) {
        return;
      }

      const locationId = String(delivery?.locationId || "").trim();
      const repName = String(
        delivery?.repName || delivery?.userId || "",
      ).trim();
      const location = locationById.get(locationId) || {};
      const hospitalCode = String(location?.clientCode || "").trim();
      const hospitalName =
        String(
          location?.name ||
            location?.locationName ||
            delivery?.locationName ||
            "",
        ).trim() || "-";

      if (
        hospitalLocationDetailAppliedFilters.locationId &&
        locationId !== hospitalLocationDetailAppliedFilters.locationId
      ) {
        return;
      }

      if (
        hospitalLocationDetailAppliedFilters.rep &&
        repName !== hospitalLocationDetailAppliedFilters.rep
      ) {
        return;
      }

      const items = Array.isArray(delivery?.items) ? delivery.items : [];

      items.forEach((item) => {
        const allocations =
          Array.isArray(item?.allocations) && item.allocations.length > 0
            ? item.allocations
            : [
                {
                  name: "Unspecified Location",
                  enteredQtyEach: resolvePositiveQty(
                    item?.effectiveQtyCases,
                    item?.enteredQtyCases,
                    item?.normalizedQtyUnits,
                  ),
                  normalizedQty: resolvePositiveQty(
                    item?.normalizedQtyUnits,
                    item?.effectiveQtyCases,
                    item?.enteredQtyCases,
                  ),
                  productCode: item?.productCode || null,
                },
              ];

        allocations.forEach((allocation) => {
          const deliveredUnits = resolvePositiveQty(
            allocation?.normalizedQty,
            allocation?.enteredQtyEach,
            item?.effectiveQtyCases,
            item?.enteredQtyCases,
          );
          if (!Number.isFinite(deliveredUnits) || deliveredUnits <= 0) {
            return;
          }

          const hospitalLocationName =
            String(allocation?.name || "").trim() || "Unspecified Location";
          const productCode = String(
            allocation?.productCode ||
              item?.productCode ||
              item?.upc ||
              item?.clientCode ||
              "",
          ).trim();
          const skuName = String(item?.skuName || item?.name || "").trim();

          rows.push({
            repName: repName || "-",
            hospitalCode: hospitalCode || "-",
            hospitalName,
            hospitalLocationName,
            deliveryDate: deliveryDate || "-",
            referenceNumber:
              String(
                delivery?.uniqueReferenceNumber || delivery?.localId || "",
              ).trim() || "-",
            productCode: productCode || "-",
            skuName: skuName || toDisplayText(item?.skuId),
            deliveredUnits,
          });
        });
      });
    });

    return rows.sort((a, b) => {
      const repCompare = a.repName.localeCompare(b.repName);
      if (repCompare !== 0) return repCompare;
      const hospitalCompare = a.hospitalCode.localeCompare(b.hospitalCode);
      if (hospitalCompare !== 0) return hospitalCompare;
      const dateCompare = a.deliveryDate.localeCompare(b.deliveryDate);
      if (dateCompare !== 0) return dateCompare;
      const skuCompare = a.skuName.localeCompare(b.skuName);
      if (skuCompare !== 0) return skuCompare;
      const locationCompare = a.hospitalLocationName.localeCompare(
        b.hospitalLocationName,
      );
      if (locationCompare !== 0) return locationCompare;
      const productCompare = a.productCode.localeCompare(b.productCode);
      if (productCompare !== 0) return productCompare;
      return a.referenceNumber.localeCompare(b.referenceNumber);
    });
  }, [
    hospitalLocationDetailHasRun,
    hospitalLocationDetailAppliedFilters,
    hospitalRows,
    salesOrders,
  ]);

  const pagedConsumptionRows = useMemo(() => {
    const sourceRows =
      selectedReportType === "consumption"
        ? consumptionRows
        : selectedReportType === "lotExpiry"
          ? lotExpiryRows
          : selectedReportType === "hospitalLocationSummary"
            ? hospitalLocationSummaryRows
            : selectedReportType === "hospitalLocationDetail"
              ? hospitalLocationDetailRows
              : [];
    const start = consumptionPage * consumptionRowsPerPage;
    return sourceRows.slice(start, start + consumptionRowsPerPage);
  }, [
    selectedReportType,
    consumptionRows,
    lotExpiryRows,
    hospitalLocationSummaryRows,
    hospitalLocationDetailRows,
    consumptionPage,
    consumptionRowsPerPage,
  ]);

  useEffect(() => {
    setConsumptionPage(0);
  }, [
    consumptionAppliedFilters,
    consumptionHasRun,
    lotExpiryAppliedFilters,
    lotExpiryHasRun,
    selectedReportType,
    consumptionRowsPerPage,
  ]);

  useEffect(() => {
    loadProject();
  }, [projectId]);

  useEffect(() => {
    if (!project?.id) return;
    loadTabData(currentTab);
  }, [project?.id, currentTab]);

  const loadProject = async () => {
    setLoading(true);
    await getProject(projectId);
    setLoading(false);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const loadPositiveSnapshotData = async () => {
    if (positiveSnapshotIncludeHistory) {
      if (!positiveSnapshotHistoryDateFrom || !positiveSnapshotHistoryDateTo) {
        setPositiveSnapshotStatus(
          "Date range is required when history is enabled.",
        );
        setPositiveSnapshotLockers([]);
        return;
      }
    }

    const snapshotResults = await executeAction({
      entityName: "StorageLocation",
      action: "getPositiveSnapshotByLocker",
      data: {
        projectId: project.id,
        includeHistory: positiveSnapshotIncludeHistory,
        historyDateFrom: positiveSnapshotHistoryDateFrom,
        historyDateTo: positiveSnapshotHistoryDateTo,
      },
    });

    const rows =
      snapshotResults?.isSuccessful && Array.isArray(snapshotResults?.data)
        ? snapshotResults.data
        : [];

    setPositiveSnapshotLockers(rows);
    setPositiveSnapshotExpanded({});
    setPositiveSnapshotStatus(
      snapshotResults?.isSuccessful
        ? ""
        : snapshotResults?.message || "Unable to load Positive snapshot data.",
    );
  };

  const handleManualSendPoReceipt = async (
    orderId,
    receiptId,
    options = {},
  ) => {
    const { resend = false } = options;
    if (!receiptId || sendingPoReceiptIds[receiptId]) return;

    setSendingPoReceiptIds((current) => ({ ...current, [receiptId]: true }));
    setPoReceiptSendStatus(
      resend ? "Resending PO receipt email..." : "Sending PO receipt email...",
    );

    try {
      const results = await executeAction({
        entityName: "PurchaseOrder",
        action: "sendReceiptEmails",
        data: {
          orderId,
          receiptId,
          resend,
        },
      });

      if (results?.isSuccessful) {
        const sentCount = Number(results?.data?.sentCount || 0);
        const failedCount = Number(results?.data?.failedCount || 0);
        const firstFailureMessage = toDisplayText(
          results?.data?.failures?.[0]?.message,
          "",
        ).trim();
        setPoReceiptSendStatus(
          `${
            resend
              ? `Receipt resend complete: ${sentCount} sent, ${failedCount} failed.`
              : `Receipt send complete: ${sentCount} sent, ${failedCount} failed.`
          }${
            failedCount > 0 && firstFailureMessage
              ? ` First error: ${firstFailureMessage}`
              : ""
          }`,
        );
        await loadTabData(1);
      } else {
        setPoReceiptSendStatus(
          results?.message ||
            (resend ? "PO receipt resend failed." : "PO receipt send failed."),
        );
      }
    } catch (err) {
      setPoReceiptSendStatus(
        err?.message ||
          (resend ? "PO receipt resend failed." : "PO receipt send failed."),
      );
    } finally {
      setSendingPoReceiptIds((current) => {
        const next = { ...current };
        delete next[receiptId];
        return next;
      });
    }
  };

  const loadTabData = async (tabIndex, options = {}) => {
    const { preserveSalesFilters = false } = options;

    if (!project?.id) return;

    setTabLoading(true);
    try {
      if (tabIndex === 1) {
        const poResults = await executeAction({
          entityName: "PurchaseOrder",
          action: "getByProject",
          data: { projectId: project.id },
        });
        const rows =
          poResults?.isSuccessful && Array.isArray(poResults?.data)
            ? poResults.data
            : [];
        setPurchaseOrders(Array.isArray(rows) ? rows : []);
        setPurchaseOrderExpanded({});
        setPoStatusFilter("notReceived");
        return;
      }

      if (tabIndex === 2) {
        const [deliveryResults, territoryRows] = await Promise.all([
          executeAction({
            entityName: "Delivery",
            action: "getByProjectWithLines",
            data: { projectId: project.id },
          }),
          getFromServer({
            entityName: "Territory",
            pipeline: "withLocations",
            filter: { projectId: project.id },
          }),
        ]);

        const rows =
          deliveryResults?.isSuccessful && Array.isArray(deliveryResults?.data)
            ? deliveryResults.data
            : [];
        setSalesOrders(rows);
        setSalesOrderExpanded({});

        const repMap = new Map();
        const territoryList = Array.isArray(territoryRows) ? territoryRows : [];
        territoryList.forEach((territory) => {
          const repId = String(territory?.userId || "").trim();
          if (!repId || repMap.has(repId)) return;

          repMap.set(repId, {
            value: repId,
            label:
              String(territory?.repName || "").trim() ||
              String(territory?.userId || "").trim(),
          });
        });

        const repOptions = [...repMap.values()].sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
        );
        setSalesRepOptions(repOptions);

        if (!preserveSalesFilters) {
          setSalesDateFrom("");
          setSalesDateTo("");
          setSalesRepFilter("");
          setSalesSkuFilter("");
          setSalesInventoryCodeFilter("");
          setSalesProductCodeFilter("");
          setSalesFiltersApplied(false);
          setAppliedSalesDateFrom("");
          setAppliedSalesDateTo("");
          setAppliedSalesRepFilter("");
          setAppliedSalesSkuFilter("");
          setAppliedSalesInventoryCodeFilter("");
          setAppliedSalesProductCodeFilter("");
        }
        return;
      }

      if (tabIndex === 3) {
        const results = await executeAction({
          entityName: "ProjectLocation",
          action: "getFullList",
          data: {
            projectId: project.id,
            currentIndex: 0,
            recordsPerPage: 250,
          },
        });

        const rows =
          results?.isSuccessful && Array.isArray(results?.data?.data)
            ? results.data.data
            : [];
        setHospitalRows(rows);
        return;
      }

      if (tabIndex === 4) {
        await loadPositiveSnapshotData();
        return;
      }

      if (tabIndex === 5) {
        setReturnTransactions([]);
        setReturnsDateFrom("");
        setReturnsDateTo("");
        setReturnsRepFilter("");
        setReturnsHospitalFilter("");
        setReturnsSkuFilter("");
        setReturnsLotNumberFilter("");
        setReturnsTypeFilter("");
        setReturnsProcessedFilter("");
        setReturnsEmailedFilter("");
        setReturnsAppliedFilters({
          dateFrom: "",
          dateTo: "",
          repId: "",
          hospitalId: "",
          skuId: "",
          lotNumber: "",
          returnType: "",
          processedStatus: "",
          emailStatus: "",
        });
        setReturnsHasRun(false);
        setReturnsQueryStatus("");
        setReturnsSlipStatus("");
        setReturnsMajorExpanded({});
        setReturnsShipmentExpanded({});
        return;
      }

      if (tabIndex === 6) {
        const [locationRows, poRows, deliveryResults, projectLocationResults] =
          await Promise.all([
            getFromServer({
              entityName: "StorageLocation",
              filter: { projectId: project.id },
            }),
            getFromServer({
              entityName: "PurchaseOrder",
              filter: { projectId: project.id },
            }),
            executeAction({
              entityName: "Delivery",
              action: "getByProjectWithLines",
              data: { projectId: project.id },
            }),
            executeAction({
              entityName: "ProjectLocation",
              action: "getFullList",
              data: {
                projectId: project.id,
                currentIndex: 0,
                recordsPerPage: 1000,
              },
            }),
          ]);

        setStorageLocations(Array.isArray(locationRows) ? locationRows : []);
        setPurchaseOrders(Array.isArray(poRows) ? poRows : []);
        setSalesOrders(
          deliveryResults?.isSuccessful && Array.isArray(deliveryResults?.data)
            ? deliveryResults.data
            : [],
        );
        setHospitalRows(
          projectLocationResults?.isSuccessful &&
            Array.isArray(projectLocationResults?.data?.data)
            ? projectLocationResults.data.data
            : [],
        );
        return;
      }

      if (tabIndex === 7 && isSysAdmin) {
        const settingsResults = await executeAction({
          entityName: "PositiveInventoryIntegration",
          action: "getSettings",
        });

        if (settingsResults?.isSuccessful && settingsResults?.data) {
          setPositiveSettings((current) => ({
            ...current,
            ...settingsResults.data,
            credentials: {
              ...current.credentials,
              ...(settingsResults.data.credentials || {}),
            },
            token: {
              ...current.token,
              ...(settingsResults.data.token || {}),
            },
            schedule: {
              ...current.schedule,
              ...(settingsResults.data.schedule || {}),
            },
          }));
        }
      }
    } catch (err) {
      console.error("Error loading inventory tab data:", err);
    } finally {
      setTabLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/inventory");
  };

  const togglePositiveSnapshotLocker = (lockerKey) => {
    setPositiveSnapshotExpanded((current) => ({
      ...current,
      [lockerKey]: !current[lockerKey],
    }));
  };

  const escapeCsvCell = (value) => {
    const text = String(value ?? "");
    if (/[",\n]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };

  const exportPositiveSnapshotCsv = () => {
    const rows = [];

    filteredPositiveSnapshotLockers.forEach((locker) => {
      const lockerName = locker?.name || "Locker";
      const repName = locker?.assignedRepName || "Unassigned";
      const items = Array.isArray(locker?.snapshotItems)
        ? locker.snapshotItems
        : [];

      items.forEach((item) => {
        rows.push({
          lockerName,
          repName,
          skuName: item?.skuName || "",
          sapNumber: item?.sapNumber || "",
          productCode: item?.productCode || item?.skuUpc || "",
          inventoryCode: item?.inventoryCode || "",
          lotNumber: item?.lotNumber || "",
          expiryDate: item?.expiryDate
            ? new Date(item.expiryDate).toISOString().slice(0, 10)
            : "",
          quantityOfficial: Number(item?.quantityOfficial || 0),
          snapshotAt: item?.snapshotAt
            ? new Date(item.snapshotAt).toISOString()
            : "",
        });
      });
    });

    if (rows.length === 0) {
      return;
    }

    const headers = [
      "Locker",
      "Rep",
      "SKU",
      "SAP#",
      "Product Code",
      "Inventory Code",
      "Lot Number",
      "Expiry Date",
      "Official Qty",
      "Snapshot At",
    ];

    const body = rows.map((row) =>
      [
        row.lockerName,
        row.repName,
        row.skuName,
        row.sapNumber,
        row.productCode,
        row.inventoryCode,
        row.lotNumber,
        row.expiryDate,
        row.quantityOfficial,
        row.snapshotAt,
      ]
        .map(escapeCsvCell)
        .join(","),
    );

    const csv = [headers.join(","), ...body].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safeProject = String(project?.name || "project")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-zA-Z0-9-_]/g, "");

    link.href = url;
    link.setAttribute(
      "download",
      `${safeProject || "project"}-positive-locker-list.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const exportConsumptionCsv = () => {
    if (!consumptionHasRun || consumptionRows.length === 0) {
      return;
    }

    const headers = [
      "Hospital Code",
      "Location Name",
      "Street Address",
      "City",
      "Province",
      "Postal",
      "Rep Name",
      "Product Code",
      "SKU Name",
      "Delivered Units",
    ];

    const body = consumptionRows.map((row) =>
      [
        row.hospitalCode,
        row.locationName,
        row.streetAddress,
        row.city,
        row.province,
        row.postal,
        row.repName,
        row.productCode,
        row.skuName,
        row.deliveredUnits,
      ]
        .map(escapeCsvCell)
        .join(","),
    );

    const csv = [headers.join(","), ...body].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safeProject = String(project?.name || "project")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-zA-Z0-9-_]/g, "");

    link.href = url;
    link.setAttribute(
      "download",
      `${safeProject || "project"}-consumption-report-${consumptionDateFrom}-to-${consumptionDateTo}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const exportLotExpiryCsv = () => {
    if (!lotExpiryHasRun || lotExpiryRows.length === 0) {
      return;
    }

    const headers = [
      "Reference Number",
      "Hospital Code",
      "Location Name",
      "Street Address",
      "City",
      "Province",
      "Postal",
      "Rep Name",
      "Delivery Date",
      "Product Code",
      "SKU Name",
      "Inventory Code",
      "Lot Number",
      "Expiry Date",
      "Delivered Units",
    ];

    const body = lotExpiryRows.map((row) =>
      [
        row.referenceNumber,
        row.hospitalCode,
        row.locationName,
        row.streetAddress,
        row.city,
        row.province,
        row.postal,
        row.repName,
        row.deliveryDate,
        row.productCode,
        row.skuName,
        row.inventoryCode,
        row.lotNumber,
        row.expiryDate,
        row.deliveredUnits,
      ]
        .map(escapeCsvCell)
        .join(","),
    );

    const csv = [headers.join(","), ...body].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safeProject = String(project?.name || "project")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-zA-Z0-9-_]/g, "");

    link.href = url;
    link.setAttribute(
      "download",
      `${safeProject || "project"}-lot-and-expiry-report-${lotExpiryAppliedFilters.dateFrom}-to-${lotExpiryAppliedFilters.dateTo}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const exportHospitalLocationSummaryCsv = () => {
    if (
      !hospitalLocationSummaryHasRun ||
      hospitalLocationSummaryRows.length === 0
    ) {
      return;
    }

    const headers = [
      "Rep Name",
      "Hospital Code",
      "Hospital Name",
      "Product Code",
      "SKU Name",
      "Hospital Location",
      "Delivered Units",
    ];

    const body = hospitalLocationSummaryRows.map((row) =>
      [
        row.repName,
        row.hospitalCode,
        row.hospitalName,
        row.productCode,
        row.skuName,
        row.hospitalLocationName,
        row.deliveredUnits,
      ]
        .map(escapeCsvCell)
        .join(","),
    );

    const csv = [headers.join(","), ...body].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safeProject = String(project?.name || "project")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-zA-Z0-9-_]/g, "");

    link.href = url;
    link.setAttribute(
      "download",
      `${safeProject || "project"}-hospital-location-summary-${hospitalLocationSummaryAppliedFilters.dateFrom}-to-${hospitalLocationSummaryAppliedFilters.dateTo}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const exportHospitalLocationDetailCsv = () => {
    if (
      !hospitalLocationDetailHasRun ||
      hospitalLocationDetailRows.length === 0
    ) {
      return;
    }

    const headers = [
      "Rep Name",
      "Hospital Code",
      "Hospital Name",
      "Delivery Date",
      "Product Code",
      "SKU Name",
      "Hospital Location",
      "Delivered Units",
    ];

    const body = hospitalLocationDetailRows.map((row) =>
      [
        row.repName,
        row.hospitalCode,
        row.hospitalName,
        row.deliveryDate,
        row.productCode,
        row.skuName,
        row.hospitalLocationName,
        row.deliveredUnits,
      ]
        .map(escapeCsvCell)
        .join(","),
    );

    const csv = [headers.join(","), ...body].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safeProject = String(project?.name || "project")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-zA-Z0-9-_]/g, "");

    link.href = url;
    link.setAttribute(
      "download",
      `${safeProject || "project"}-hospital-location-detail-${hospitalLocationDetailAppliedFilters.dateFrom}-to-${hospitalLocationDetailAppliedFilters.dateTo}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const exportReturnsCsv = () => {
    if (!returnsHasRun || filteredReturnTransactions.length === 0) {
      return;
    }

    const headers = [
      "Return Date",
      "Rep",
      "Hospital",
      "Inventory Code",
      "SKU",
      "Product Code",
      "Lot Number",
      "Expiry Date",
      "Qty",
      "Reason",
      "Processed Status",
      "Processed At",
      "Shipment ID",
      "Email Status",
      "Email Sent At",
      "Email Last Error",
      "Notes",
    ];

    const body = filteredReturnTransactions.map((row) => {
      const repLabel = `${String(row?.user?.firstName || "").trim()} ${String(
        row?.user?.lastName || "",
      ).trim()}`.trim();

      return [
        row?.transactionDate
          ? new Date(row.transactionDate).toISOString().slice(0, 10)
          : "",
        repLabel || row?.user?.email || row?.performedBy || row?.userId || "",
        row?.hospitalLocation?.name ||
          row?.returnLocation?.name ||
          row?.locker?.name ||
          row?.hospitalLocationId ||
          "",
        row?.inventoryCode || "",
        row?.sku?.name || row?.skuId || "",
        row?.sku?.code || "",
        row?.lotNumber || "",
        row?.expiryDate
          ? new Date(row.expiryDate).toISOString().slice(0, 10)
          : "",
        Number(row?.quantity || 0),
        row?.reason || "",
        row?.returnProcessingStatus || "",
        row?.returnHeaderProcessedAt
          ? new Date(row.returnHeaderProcessedAt).toISOString()
          : "",
        row?.returnHeaderProcessedShipmentId || "",
        row?.returnShipmentEmailStatus || "",
        row?.returnShipmentEmailSentAt
          ? new Date(row.returnShipmentEmailSentAt).toISOString()
          : "",
        row?.returnShipmentEmailLastError || "",
        row?.notes || "",
      ]
        .map(escapeCsvCell)
        .join(",");
    });

    const csv = [headers.join(","), ...body].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safeProject = String(project?.name || "project")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-zA-Z0-9-_]/g, "");

    link.href = url;
    link.setAttribute(
      "download",
      `${safeProject || "project"}-hospital-returns.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const toggleSalesDeliveryExpanded = (deliveryKey) => {
    setSalesOrderExpanded((current) => ({
      ...current,
      [deliveryKey]: !current[deliveryKey],
    }));
  };

  const applySalesFilters = () => {
    setAppliedSalesDateFrom(salesDateFrom);
    setAppliedSalesDateTo(salesDateTo);
    setAppliedSalesRepFilter(salesRepFilter);
    setAppliedSalesSkuFilter(salesSkuFilter);
    setAppliedSalesInventoryCodeFilter(salesInventoryCodeFilter);
    setAppliedSalesProductCodeFilter(salesProductCodeFilter);
    setSalesFiltersApplied(true);
  };

  const clearSalesFilters = () => {
    setSalesDateFrom("");
    setSalesDateTo("");
    setSalesRepFilter("");
    setSalesSkuFilter("");
    setSalesInventoryCodeFilter("");
    setSalesProductCodeFilter("");
    setSalesFiltersApplied(false);
    setAppliedSalesDateFrom("");
    setAppliedSalesDateTo("");
    setAppliedSalesRepFilter("");
    setAppliedSalesSkuFilter("");
    setAppliedSalesInventoryCodeFilter("");
    setAppliedSalesProductCodeFilter("");
  };

  const runReturnsQuery = async () => {
    const nextFilters = {
      dateFrom: String(returnsDateFrom || "").trim(),
      dateTo: String(returnsDateTo || "").trim(),
      repId: String(returnsRepFilter || "").trim(),
      hospitalId: String(returnsHospitalFilter || "").trim(),
      skuId: String(returnsSkuFilter || "").trim(),
      lotNumber: String(returnsLotNumberFilter || "").trim(),
      returnType: String(returnsTypeFilter || "").trim(),
      processedStatus: String(returnsProcessedFilter || "").trim(),
      emailStatus: String(returnsEmailedFilter || "").trim(),
    };

    const hasAnyFilter = Object.values(nextFilters).some(
      (value) => String(value).trim().length > 0,
    );

    if (!hasAnyFilter) {
      setReturnsHasRun(false);
      setReturnTransactions([]);
      setReturnsQueryStatus("Set at least one filter, then click Execute.");
      return;
    }

    if (
      nextFilters.dateFrom &&
      nextFilters.dateTo &&
      new Date(nextFilters.dateFrom) > new Date(nextFilters.dateTo)
    ) {
      setReturnsQueryStatus("Date From cannot be after Date To.");
      return;
    }

    setTabLoading(true);
    setReturnsQueryStatus("Loading returns...");
    setReturnsSlipStatus("");
    setReturnsMajorExpanded({});
    setReturnsShipmentExpanded({});

    try {
      const results = await executeAction({
        entityName: "ReturnHeader",
        action: "getHospitalReturnsGrid",
        data: {
          projectId: project.id,
          startDate: nextFilters.dateFrom || undefined,
          endDate: nextFilters.dateTo || undefined,
          userId: nextFilters.repId || undefined,
          hospitalId: nextFilters.hospitalId || undefined,
          skuId: nextFilters.skuId || undefined,
          lotNumber: nextFilters.lotNumber || undefined,
          returnType: nextFilters.returnType || undefined,
        },
      });

      const rows =
        results?.isSuccessful && Array.isArray(results?.data)
          ? results.data
          : [];

      setReturnTransactions(rows);
      setReturnsAppliedFilters(nextFilters);
      setReturnsHasRun(!!results?.isSuccessful);

      if (!results?.isSuccessful) {
        setReturnsQueryStatus(results?.message || "Failed to load returns.");
      } else if (rows.length === 0) {
        setReturnsQueryStatus("No returns found for the selected filters.");
      } else {
        setReturnsQueryStatus("");
      }
    } catch (err) {
      setReturnTransactions([]);
      setReturnsHasRun(false);
      setReturnsQueryStatus(err?.message || "Failed to load returns.");
    } finally {
      setTabLoading(false);
    }
  };

  const clearReturnsFilters = () => {
    setReturnsDateFrom("");
    setReturnsDateTo("");
    setReturnsRepFilter("");
    setReturnsHospitalFilter("");
    setReturnsSkuFilter("");
    setReturnsLotNumberFilter("");
    setReturnsTypeFilter("");
    setReturnsProcessedFilter("");
    setReturnsEmailedFilter("");
    setReturnsAppliedFilters({
      dateFrom: "",
      dateTo: "",
      repId: "",
      hospitalId: "",
      skuId: "",
      lotNumber: "",
      returnType: "",
      processedStatus: "",
      emailStatus: "",
    });
    setReturnsHasRun(false);
    setReturnsQueryStatus("");
    setReturnsSlipStatus("");
    setReturnTransactions([]);
    setReturnsMajorExpanded({});
    setReturnsShipmentExpanded({});
  };

  const recreateShipmentPackingSlip = async ({ shipmentId, rows, label }) => {
    if (!project?.id) {
      setReturnsSlipStatus("Project context is missing.");
      return;
    }

    const returnHeaderIds = [
      ...new Set(
        (rows || [])
          .map((row) => String(row?.returnHeaderId || "").trim())
          .filter((value) => value.length > 0),
      ),
    ];

    if (!shipmentId && returnHeaderIds.length === 0) {
      setReturnsSlipStatus("No returns available for slip preview.");
      return;
    }

    const previewWindow = window.open("", "_blank");
    if (!previewWindow) {
      setReturnsSlipStatus(
        "Unable to open preview window. Please allow pop-ups and try again.",
      );
      return;
    }

    previewWindow.document.write(
      "<html><body style='font-family:Arial,sans-serif;padding:16px;'>Generating packing slip preview...</body></html>",
    );
    previewWindow.document.close();

    setReturnsSlipStatus(
      `Generating packing slip preview for ${label || "shipment"}...`,
    );

    try {
      const results = await executeAction({
        entityName: "ReturnHeader",
        action: "getPackingSlipPreview",
        data: {
          projectId: project.id,
          processedShipmentId: shipmentId || undefined,
          returnHeaderIds: shipmentId ? undefined : returnHeaderIds,
        },
      });

      if (!results?.isSuccessful || !results?.data?.html) {
        previewWindow.close();
        setReturnsSlipStatus(
          results?.message || "Failed to generate packing slip preview.",
        );
        return;
      }

      previewWindow.document.open();
      previewWindow.document.write(results.data.html);
      previewWindow.document.close();

      setReturnsSlipStatus(
        `Packing slip preview generated for ${results?.data?.shipmentLabel || label || "shipment"}.`,
      );
    } catch (err) {
      previewWindow.close();
      setReturnsSlipStatus(
        err?.message || "Failed to generate packing slip preview.",
      );
    }
  };

  const openAdjustmentModal = (delivery, item) => {
    setSelectedDeliveryLine({
      deliveryId: delivery?.id || null,
      deliveryLineId: item?.lineId || null,
      skuName: item?.skuName || item?.skuId || "-",
      inventoryCode: item?.inventoryCode || item?.clientCode || "-",
      productCode: item?.productCode || item?.upc || "-",
      lotNumber: item?.lotNumber || "-",
      originalQtyCases: Number(item?.enteredQtyCases ?? 0) || 0,
      currentEffectiveQtyCases:
        Number(item?.effectiveQtyCases ?? item?.enteredQtyCases ?? 0) || 0,
    });
    setAdjustedQtyCasesInput(
      String(Number(item?.effectiveQtyCases ?? item?.enteredQtyCases ?? 0)),
    );
    setAdjustmentReasonInput("");
    setAdjustmentNotesInput("");
    setAdjustmentStatus("");
    setAdjustmentModalOpen(true);
  };

  const closeAdjustmentModal = () => {
    setAdjustmentModalOpen(false);
    setSelectedDeliveryLine(null);
    setAdjustedQtyCasesInput("");
    setAdjustmentReasonInput("");
    setAdjustmentNotesInput("");
    setAdjustmentStatus("");
  };

  const saveDeliveryAdjustment = async () => {
    const qty = Number(adjustedQtyCasesInput);
    if (!Number.isFinite(qty) || qty < 0) {
      setAdjustmentStatus("Adjusted cases must be a number >= 0.");
      return;
    }

    if (!selectedDeliveryLine?.deliveryLineId) {
      setAdjustmentStatus("Delivery line not found.");
      return;
    }

    setAdjustmentStatus("Saving adjustment...");

    const results = await executeAction({
      entityName: "DeliveryLineAdjustment",
      action: "createForDeliveryLine",
      data: {
        deliveryLineId: selectedDeliveryLine.deliveryLineId,
        adjustedQtyCases: qty,
        reason: adjustmentReasonInput,
        notes: adjustmentNotesInput,
      },
    });

    if (!results?.isSuccessful) {
      setAdjustmentStatus(results?.message || "Failed to save adjustment.");
      return;
    }

    await loadTabData(2, { preserveSalesFilters: true });
    closeAdjustmentModal();
  };

  const togglePurchaseOrderExpanded = (orderKey) => {
    setPurchaseOrderExpanded((current) => ({
      ...current,
      [orderKey]: !current[orderKey],
    }));
  };

  const getOrderedByLabel = (order) => {
    const label = getOrderRepLabel(order);
    if (label) return label;
    return toDisplayText(order?.orderedBy);
  };

  const getPurchaseOrderSkuLabel = (order, item) => {
    const itemSkuId = toDisplayText(item?.skuId, "").trim();
    const skuDetails = Array.isArray(order?.skuDetails) ? order.skuDetails : [];

    const matchedSku = skuDetails.find((sku) => {
      const skuId = toDisplayText(sku?.id || sku?._id, "").trim();
      return skuId && itemSkuId && skuId === itemSkuId;
    });

    if (matchedSku) {
      const code = toDisplayText(
        matchedSku?.code || matchedSku?.clientCode,
        "",
      ).trim();
      const name = toDisplayText(matchedSku?.name, "").trim();
      if (code && name) return `${code} - ${name}`;
      if (name) return name;
      if (code) return code;
    }

    return toDisplayText(item?.skuId);
  };

  const getPurchaseOrderSkuDetails = (order, item) => {
    const itemSkuId = toDisplayText(item?.skuId, "").trim();
    const skuDetails = Array.isArray(order?.skuDetails) ? order.skuDetails : [];

    const matchedSku = skuDetails.find((sku) => {
      const skuId = toDisplayText(sku?.id || sku?._id, "").trim();
      return skuId && itemSkuId && skuId === itemSkuId;
    });

    const productCode = toDisplayText(
      matchedSku?.code || matchedSku?.clientCode,
      "",
    ).trim();
    const sapNumber = getSkuSapNumber(matchedSku);

    return {
      matchedSku,
      productCode,
      sapNumber,
      skuLabel: getPurchaseOrderSkuLabel(order, item),
    };
  };

  const lockerQuantityTotal = storageLocations.reduce(
    (sum, row) => sum + Number(row?.totalQuantity ?? row?.currentQuantity ?? 0),
    0,
  );

  const savePositiveCredentials = async () => {
    setPositiveSettingsStatus("Saving credentials...");
    const result = await executeAction({
      entityName: "PositiveInventoryIntegration",
      action: "saveCredentials",
      data: positiveSettings.credentials,
    });

    if (result?.isSuccessful) {
      setPositiveSettings((current) => ({
        ...current,
        credentials: {
          ...current.credentials,
          ...(result.data || {}),
        },
      }));
      setPositiveSettingsStatus("Credentials saved.");
    } else {
      setPositiveSettingsStatus(
        result?.message || "Failed to save credentials.",
      );
    }
  };

  const savePositiveSchedule = async () => {
    setPositiveSettingsStatus("Saving schedule...");
    const result = await executeAction({
      entityName: "PositiveInventoryIntegration",
      action: "saveSchedule",
      data: positiveSettings.schedule,
    });

    if (result?.isSuccessful) {
      setPositiveSettings((current) => ({
        ...current,
        schedule: {
          ...current.schedule,
          ...(result.data || {}),
        },
      }));
      setPositiveSettingsStatus("Schedule saved.");
    } else {
      setPositiveSettingsStatus(result?.message || "Failed to save schedule.");
    }
  };

  const savePositiveToken = async () => {
    setPositiveSettingsStatus("Saving token...");
    const result = await executeAction({
      entityName: "PositiveInventoryIntegration",
      action: "saveToken",
      data: positiveSettings.token,
    });

    if (result?.isSuccessful) {
      setPositiveSettings((current) => ({
        ...current,
        ...result.data,
      }));
      setPositiveSettingsStatus("Token saved.");
    } else {
      setPositiveSettingsStatus(result?.message || "Failed to save token.");
    }
  };

  const refreshPositiveToken = async () => {
    setPositiveSettingsStatus("Refreshing token...");
    const result = await executeAction({
      entityName: "PositiveInventoryIntegration",
      action: "refreshToken",
    });

    if (result?.isSuccessful) {
      setPositiveSettings((current) => ({
        ...current,
        token: {
          ...current.token,
          ...(result.data || {}),
        },
      }));
      setPositiveSettingsStatus("Token refreshed.");
    } else {
      setPositiveSettingsStatus(result?.message || "Token refresh failed.");
    }
  };

  const runPositiveSyncNow = async () => {
    setPositiveSettingsStatus("Running Positive inventory sync...");
    const result = await executeAction({
      entityName: "PositiveInventoryIntegration",
      action: "runNow",
    });

    if (result?.isSuccessful) {
      const runSummary = result?.data || {};
      setPositiveSettings((current) => ({
        ...current,
        latestRun: {
          id: runSummary.runId || current.latestRun?.id,
          status: "completed",
          startedAt: current.latestRun?.startedAt || null,
          completedAt: new Date().toISOString(),
          recordsRead: runSummary.recordsRead || 0,
          recordsAccepted: runSummary.recordsAccepted || 0,
          errorCount: Array.isArray(runSummary.warnings)
            ? runSummary.warnings.length
            : 0,
        },
      }));
      setPositiveSettingsStatus(
        runSummary?.supportTicketId
          ? `Sync completed with ticket ${runSummary.supportTicketId}.`
          : "Sync completed.",
      );
    } else {
      setPositiveSettingsStatus(result?.message || "Sync failed.");
    }
  };

  const runConsumptionReport = () => {
    if (!hasConsumptionDateRangeInput) {
      return;
    }

    setConsumptionAppliedFilters({
      dateFrom: consumptionDateFrom,
      dateTo: consumptionDateTo,
      locationId: consumptionHospitalCodeFilter,
      rep: consumptionRepFilter,
    });
    setConsumptionHasRun(true);
    setConsumptionPage(0);
  };

  const runLotExpiryReport = () => {
    if (!hasConsumptionDateRangeInput) {
      return;
    }

    setLotExpiryAppliedFilters({
      dateFrom: consumptionDateFrom,
      dateTo: consumptionDateTo,
      locationId: consumptionHospitalCodeFilter,
      rep: consumptionRepFilter,
      inventoryCode: lotExpiryInventoryCodeFilter,
    });
    setLotExpiryHasRun(true);
    setConsumptionPage(0);
  };

  const runHospitalLocationSummaryReport = () => {
    if (!hasConsumptionDateRangeInput) {
      return;
    }

    setHospitalLocationSummaryAppliedFilters({
      dateFrom: consumptionDateFrom,
      dateTo: consumptionDateTo,
      locationId: consumptionHospitalCodeFilter,
      rep: consumptionRepFilter,
    });
    setHospitalLocationSummaryHasRun(true);
    setConsumptionPage(0);
  };

  const runHospitalLocationDetailReport = () => {
    if (!hasConsumptionDateRangeInput) {
      return;
    }

    setHospitalLocationDetailAppliedFilters({
      dateFrom: consumptionDateFrom,
      dateTo: consumptionDateTo,
      locationId: consumptionHospitalCodeFilter,
      rep: consumptionRepFilter,
    });
    setHospitalLocationDetailHasRun(true);
    setConsumptionPage(0);
  };

  const getSelectedReportLabel = () => {
    if (selectedReportType === "consumption") {
      return "Consumption Report";
    }
    if (selectedReportType === "lotExpiry") {
      return "Lot and Expiry Report";
    }
    if (selectedReportType === "hospitalLocationSummary") {
      return "Hospital Location Summary";
    }
    if (selectedReportType === "hospitalLocationDetail") {
      return "Hospital Location Detail";
    }
    return "Report";
  };

  const runSelectedReport = () => {
    if (selectedReportType === "consumption") {
      runConsumptionReport();
      return;
    }

    if (selectedReportType === "lotExpiry") {
      runLotExpiryReport();
      return;
    }

    if (selectedReportType === "hospitalLocationSummary") {
      runHospitalLocationSummaryReport();
      return;
    }

    if (selectedReportType === "hospitalLocationDetail") {
      runHospitalLocationDetailReport();
    }
  };

  const exportSelectedReport = () => {
    if (selectedReportType === "consumption") {
      exportConsumptionCsv();
      return;
    }

    if (selectedReportType === "lotExpiry") {
      exportLotExpiryCsv();
      return;
    }

    if (selectedReportType === "hospitalLocationSummary") {
      exportHospitalLocationSummaryCsv();
      return;
    }

    if (selectedReportType === "hospitalLocationDetail") {
      exportHospitalLocationDetailCsv();
    }
  };

  const isRunDisabled = !hasConsumptionDateRangeInput;

  const isExportDisabled =
    selectedReportType === "consumption"
      ? !consumptionHasRun || consumptionRows.length === 0
      : selectedReportType === "lotExpiry"
        ? !lotExpiryHasRun || lotExpiryRows.length === 0
        : selectedReportType === "hospitalLocationSummary"
          ? !hospitalLocationSummaryHasRun ||
            hospitalLocationSummaryRows.length === 0
          : selectedReportType === "hospitalLocationDetail"
            ? !hospitalLocationDetailHasRun ||
              hospitalLocationDetailRows.length === 0
            : true;

  const selectedReportHasRun =
    selectedReportType === "consumption"
      ? consumptionHasRun
      : selectedReportType === "lotExpiry"
        ? lotExpiryHasRun
        : selectedReportType === "hospitalLocationSummary"
          ? hospitalLocationSummaryHasRun
          : selectedReportType === "hospitalLocationDetail"
            ? hospitalLocationDetailHasRun
            : false;

  const selectedReportRows =
    selectedReportType === "consumption"
      ? consumptionRows
      : selectedReportType === "lotExpiry"
        ? lotExpiryRows
        : selectedReportType === "hospitalLocationSummary"
          ? hospitalLocationSummaryRows
          : selectedReportType === "hospitalLocationDetail"
            ? hospitalLocationDetailRows
            : [];

  if (loading || !project) {
    return (
      <Page
        pageName="inventory"
        includeToolbar={false}
      >
        <Box sx={{ p: 3 }}>
          <Typography>Loading...</Typography>
        </Box>
      </Page>
    );
  }

  return (
    <Page
      pageName="inventory"
      includeToolbar={false}
    >
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Back to Projects
          </Button>
          <Typography variant="h5">{project.name}</Typography>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
          >
            <Tab label="Lockers" />
            <Tab label="Purchase Orders" />
            <Tab label="Hospital Deliveries" />
            <Tab label="Hospitals" />
            <Tab label="Positive Snapshot" />
            <Tab label="Returns" />
            <Tab label="Reports" />
            {isSysAdmin && <Tab label="Positive API" />}
          </Tabs>
        </Box>

        <Box>
          {currentTab === 0 && (
            <StorageLocations
              clientId={project.clientId}
              projectId={project.id}
            />
          )}

          {currentTab === 1 && (
            <Box>
              <Typography
                variant="h6"
                sx={{ mb: 2 }}
              >
                Purchase Orders
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <TextField
                  label="SKU"
                  size="small"
                  value={poSkuFilter}
                  onChange={(event) => setPoSkuFilter(event.target.value)}
                />
                <TextField
                  label="Product Code"
                  size="small"
                  value={poProductCodeFilter}
                  onChange={(event) =>
                    setPoProductCodeFilter(event.target.value)
                  }
                />
                <TextField
                  label="Lot Number"
                  size="small"
                  value={poLotNumberFilter}
                  onChange={(event) => setPoLotNumberFilter(event.target.value)}
                />
                <FormControl size="small">
                  <InputLabel id="po-rep-filter-label">Rep</InputLabel>
                  <Select
                    labelId="po-rep-filter-label"
                    label="Rep"
                    value={poRepFilter}
                    onChange={(event) => setPoRepFilter(event.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {purchaseOrderFilterOptions.repOptions.map((repOption) => (
                      <MenuItem
                        key={repOption}
                        value={repOption}
                      >
                        {repOption}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small">
                  <InputLabel id="po-locker-filter-label">Locker</InputLabel>
                  <Select
                    labelId="po-locker-filter-label"
                    label="Locker"
                    value={poLockerFilter}
                    onChange={(event) => setPoLockerFilter(event.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {purchaseOrderFilterOptions.lockerOptions.map(
                      (lockerOption) => (
                        <MenuItem
                          key={lockerOption}
                          value={lockerOption}
                        >
                          {lockerOption}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                </FormControl>
                <TextField
                  label="Date From"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={poDateFromFilter}
                  onChange={(event) => setPoDateFromFilter(event.target.value)}
                />
                <TextField
                  label="Date To"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={poDateToFilter}
                  onChange={(event) => setPoDateToFilter(event.target.value)}
                />
                <FormControl size="small">
                  <InputLabel id="po-status-filter-label">Status</InputLabel>
                  <Select
                    labelId="po-status-filter-label"
                    label="Status"
                    value={poStatusFilter}
                    onChange={(event) => setPoStatusFilter(event.target.value)}
                  >
                    <MenuItem value="notReceived">Not Received</MenuItem>
                    <MenuItem value="">All</MenuItem>
                    {purchaseOrderFilterOptions.statusOptions.map(
                      (statusOption) => (
                        <MenuItem
                          key={statusOption}
                          value={statusOption}
                        >
                          {statusOption}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                </FormControl>
              </Box>
              {poReceiptSendStatus && (
                <Typography
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {poReceiptSendStatus}
                </Typography>
              )}

              {tabLoading ? (
                <Typography>Loading...</Typography>
              ) : purchaseOrders.length === 0 ? (
                <Card sx={{ p: 2 }}>
                  <Typography color="text.secondary">
                    No purchase orders for this project.
                  </Typography>
                </Card>
              ) : filteredPurchaseOrders.length === 0 ? (
                <Card sx={{ p: 2 }}>
                  <Typography color="text.secondary">
                    No purchase orders match the current filters.
                  </Typography>
                </Card>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {filteredPurchaseOrders.map((order, index) => {
                    const orderKey = order.id || `po-${index}`;
                    const isExpanded = !!purchaseOrderExpanded[orderKey];
                    const items = Array.isArray(order?.items)
                      ? order.items
                      : [];
                    const receipts = Array.isArray(order?.receipts)
                      ? [...order.receipts].sort((a, b) => {
                          const aTime = a?.receivedAt
                            ? new Date(a.receivedAt).getTime()
                            : 0;
                          const bTime = b?.receivedAt
                            ? new Date(b.receivedAt).getTime()
                            : 0;
                          return bTime - aTime;
                        })
                      : [];

                    return (
                      <Card
                        key={orderKey}
                        sx={{ p: 2 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700 }}>
                            {toDisplayText(order.orderNumber)}
                          </Typography>
                          <Button
                            size="small"
                            onClick={() =>
                              togglePurchaseOrderExpanded(orderKey)
                            }
                          >
                            {isExpanded ? "Collapse" : "Expand"}
                          </Button>
                        </Box>

                        <Typography
                          color="text.secondary"
                          sx={{ mb: isExpanded ? 2 : 0 }}
                        >
                          Ordered By: {getOrderedByLabel(order)} | Status:{" "}
                          {toDisplayText(order.status)} | Order Date:{" "}
                          {order.orderDate
                            ? new Date(order.orderDate).toLocaleDateString()
                            : "-"}{" "}
                          | Sent Date:{" "}
                          {order.knnEmailSentAt
                            ? new Date(
                                order.knnEmailSentAt,
                              ).toLocaleDateString()
                            : "-"}{" "}
                          | Total Cost: {toDisplayText(order.totalCost, "0")} |
                          Lines: {items.length}
                        </Typography>

                        {isExpanded && (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Product Code</TableCell>
                                    <TableCell>SAP #</TableCell>
                                    <TableCell>SKU</TableCell>
                                    <TableCell>Qty Ordered</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {items.length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={4}>
                                        No line items on this purchase order.
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    items.map((item, itemIndex) => {
                                      const skuDetails =
                                        getPurchaseOrderSkuDetails(order, item);

                                      return (
                                        <TableRow
                                          key={`${orderKey}-line-${itemIndex}`}
                                        >
                                          <TableCell>
                                            {toDisplayText(
                                              skuDetails.productCode,
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {toDisplayText(
                                              skuDetails.sapNumber,
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {skuDetails.skuLabel}
                                          </TableCell>
                                          <TableCell>
                                            {toDisplayText(
                                              item?.quantityOrdered,
                                              "0",
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>

                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ mb: 1 }}
                              >
                                Receipts Against This PO
                              </Typography>
                              <TableContainer>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Received Date</TableCell>
                                      <TableCell>Status</TableCell>
                                      <TableCell>Lines</TableCell>
                                      <TableCell>Packing Slip</TableCell>
                                      <TableCell align="right">
                                        Action
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {receipts.length === 0 ? (
                                      <TableRow>
                                        <TableCell colSpan={5}>
                                          No receipts for this purchase order.
                                        </TableCell>
                                      </TableRow>
                                    ) : (
                                      receipts.map((receipt, receiptIndex) => {
                                        const receiptId = toDisplayText(
                                          receipt?.id || receipt?._id,
                                          "",
                                        ).trim();
                                        const orderId = toDisplayText(
                                          order?.id || order?._id,
                                          "",
                                        ).trim();
                                        const receiptStatus = toDisplayText(
                                          receipt?.positiveHandoffStatus,
                                        );
                                        const sentTimestamp =
                                          receiptStatus === "slipSentEmail" &&
                                          receipt?.updatedAt
                                            ? new Date(
                                                receipt.updatedAt,
                                              ).toLocaleString()
                                            : "";
                                        const receiptLines = Array.isArray(
                                          receipt?.lines,
                                        )
                                          ? [...receipt.lines].sort((a, b) => {
                                              const aSkuDetails =
                                                getPurchaseOrderSkuDetails(
                                                  order,
                                                  {
                                                    skuId: a?.skuId,
                                                  },
                                                );
                                              const bSkuDetails =
                                                getPurchaseOrderSkuDetails(
                                                  order,
                                                  {
                                                    skuId: b?.skuId,
                                                  },
                                                );

                                              const aProductCode =
                                                toDisplayText(
                                                  aSkuDetails?.productCode,
                                                  "",
                                                ).trim();
                                              const bProductCode =
                                                toDisplayText(
                                                  bSkuDetails?.productCode,
                                                  "",
                                                ).trim();

                                              return aProductCode.localeCompare(
                                                bProductCode,
                                                undefined,
                                                {
                                                  numeric: true,
                                                  sensitivity: "base",
                                                },
                                              );
                                            })
                                          : [];
                                        const canSend =
                                          receiptStatus === "pendingSlip" &&
                                          !!String(
                                            receipt?.packingSlipUrl || "",
                                          ).trim();
                                        const canResend =
                                          isSysAdmin &&
                                          receiptStatus === "slipSentEmail" &&
                                          !!String(
                                            receipt?.packingSlipUrl || "",
                                          ).trim();
                                        const isSending =
                                          !!sendingPoReceiptIds[receiptId];

                                        return (
                                          <Fragment
                                            key={`${orderKey}-receipt-fragment-${receiptId || receiptIndex}`}
                                          >
                                            <TableRow
                                              key={`${orderKey}-receipt-${receiptId || receiptIndex}`}
                                            >
                                              <TableCell>
                                                {receipt?.receivedAt
                                                  ? new Date(
                                                      receipt.receivedAt,
                                                    ).toLocaleDateString()
                                                  : "-"}
                                              </TableCell>
                                              <TableCell>
                                                {receiptStatus}
                                              </TableCell>
                                              <TableCell>
                                                {toDisplayText(
                                                  receipt?.lineCount,
                                                  "0",
                                                )}
                                              </TableCell>
                                              <TableCell>
                                                {receipt?.packingSlipUrl
                                                  ? "Yes"
                                                  : "No"}
                                              </TableCell>
                                              <TableCell align="right">
                                                {canSend ? (
                                                  <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={isSending}
                                                    onClick={() =>
                                                      handleManualSendPoReceipt(
                                                        orderId,
                                                        receiptId,
                                                      )
                                                    }
                                                  >
                                                    {isSending
                                                      ? "Sending..."
                                                      : "Send Receipt"}
                                                  </Button>
                                                ) : canResend ? (
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      flexDirection: "column",
                                                      alignItems: "flex-end",
                                                      gap: 0.5,
                                                    }}
                                                  >
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                    >
                                                      {sentTimestamp
                                                        ? `Sent ${sentTimestamp}`
                                                        : "Sent"}
                                                    </Typography>
                                                    <Button
                                                      size="small"
                                                      variant="outlined"
                                                      color="warning"
                                                      disabled={isSending}
                                                      onClick={() =>
                                                        setResendConfirmReceipt(
                                                          {
                                                            orderId,
                                                            receiptId,
                                                          },
                                                        )
                                                      }
                                                    >
                                                      {isSending
                                                        ? "Resending..."
                                                        : "Resend Receipt"}
                                                    </Button>
                                                  </Box>
                                                ) : (
                                                  <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                  >
                                                    {receiptStatus ===
                                                    "slipSentEmail"
                                                      ? sentTimestamp
                                                        ? `Sent ${sentTimestamp}`
                                                        : "Sent"
                                                      : receipt?.packingSlipUrl
                                                        ? "Not eligible"
                                                        : "Missing slip"}
                                                  </Typography>
                                                )}
                                              </TableCell>
                                            </TableRow>
                                            <TableRow
                                              key={`${orderKey}-receipt-lines-${receiptId || receiptIndex}`}
                                            >
                                              <TableCell
                                                colSpan={5}
                                                sx={{ py: 0.5 }}
                                              >
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                  sx={{
                                                    display: "block",
                                                    mb: 0.5,
                                                  }}
                                                >
                                                  Receipt Lines
                                                </Typography>
                                                {receiptLines.length === 0 ? (
                                                  <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                  >
                                                    No receipt lines recorded.
                                                  </Typography>
                                                ) : (
                                                  <TableContainer>
                                                    <Table size="small">
                                                      <TableHead>
                                                        <TableRow>
                                                          <TableCell>
                                                            Product Code
                                                          </TableCell>
                                                          <TableCell>
                                                            SAP #
                                                          </TableCell>
                                                          <TableCell>
                                                            SKU
                                                          </TableCell>
                                                          <TableCell>
                                                            Cases
                                                          </TableCell>
                                                          <TableCell>
                                                            Lot
                                                          </TableCell>
                                                          <TableCell>
                                                            Expiry
                                                          </TableCell>
                                                        </TableRow>
                                                      </TableHead>
                                                      <TableBody>
                                                        {receiptLines.map(
                                                          (line, lineIndex) => {
                                                            const skuDetails =
                                                              getPurchaseOrderSkuDetails(
                                                                order,
                                                                {
                                                                  skuId:
                                                                    line?.skuId,
                                                                },
                                                              );

                                                            return (
                                                              <TableRow
                                                                key={`${orderKey}-receipt-${receiptId || receiptIndex}-line-${lineIndex}`}
                                                              >
                                                                <TableCell>
                                                                  {toDisplayText(
                                                                    skuDetails.productCode,
                                                                  )}
                                                                </TableCell>
                                                                <TableCell>
                                                                  {toDisplayText(
                                                                    skuDetails.sapNumber,
                                                                  )}
                                                                </TableCell>
                                                                <TableCell>
                                                                  {
                                                                    skuDetails.skuLabel
                                                                  }
                                                                </TableCell>
                                                                <TableCell>
                                                                  {toDisplayText(
                                                                    line?.enteredQty,
                                                                    "0",
                                                                  )}
                                                                </TableCell>
                                                                <TableCell>
                                                                  {toDisplayText(
                                                                    line?.lotNumber,
                                                                  )}
                                                                </TableCell>
                                                                <TableCell>
                                                                  {toYyyyMmDd(
                                                                    line?.expiryDate,
                                                                  )}
                                                                </TableCell>
                                                              </TableRow>
                                                            );
                                                          },
                                                        )}
                                                      </TableBody>
                                                    </Table>
                                                  </TableContainer>
                                                )}
                                              </TableCell>
                                            </TableRow>
                                          </Fragment>
                                        );
                                      })
                                    )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          </Box>
                        )}
                      </Card>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}

          {currentTab === 2 && (
            <Box>
              <Typography
                variant="h6"
                sx={{ mb: 2 }}
              >
                Deliveries
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                Delivery headers with expandable line-item details.
              </Typography>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
                <TextField
                  size="small"
                  type="date"
                  label="Date From"
                  value={salesDateFrom}
                  onChange={(event) => setSalesDateFrom(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 180 }}
                />

                <TextField
                  size="small"
                  type="date"
                  label="Date To"
                  value={salesDateTo}
                  onChange={(event) => setSalesDateTo(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 180 }}
                />

                <FormControl
                  size="small"
                  sx={{ minWidth: 220 }}
                >
                  <InputLabel id="sales-rep-filter-label">Rep</InputLabel>
                  <Select
                    labelId="sales-rep-filter-label"
                    value={salesRepFilter}
                    label="Rep"
                    onChange={(event) => setSalesRepFilter(event.target.value)}
                  >
                    <MenuItem value="">All Reps</MenuItem>
                    {salesRepOptions.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{ minWidth: 240 }}
                >
                  <InputLabel id="sales-sku-filter-label">SKU</InputLabel>
                  <Select
                    labelId="sales-sku-filter-label"
                    value={salesSkuFilter}
                    label="SKU"
                    onChange={(event) => setSalesSkuFilter(event.target.value)}
                  >
                    <MenuItem value="">All SKUs</MenuItem>
                    {salesSkuOptions.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  label="Inventory Code"
                  value={salesInventoryCodeFilter}
                  onChange={(event) =>
                    setSalesInventoryCodeFilter(event.target.value)
                  }
                  sx={{ minWidth: 220 }}
                />

                <TextField
                  size="small"
                  label="Product Code"
                  value={salesProductCodeFilter}
                  onChange={(event) =>
                    setSalesProductCodeFilter(event.target.value)
                  }
                  sx={{ minWidth: 220 }}
                />

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={applySalesFilters}
                  >
                    Apply
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={clearSalesFilters}
                  >
                    Clear
                  </Button>
                </Box>
              </Box>

              {tabLoading ? (
                <Typography>Loading...</Typography>
              ) : !salesFiltersApplied ? (
                <Card sx={{ p: 2 }}>
                  <Typography color="text.secondary">
                    Set delivery filters and click Apply to view results.
                  </Typography>
                </Card>
              ) : filteredSalesDeliveries.length === 0 ? (
                <Card sx={{ p: 2 }}>
                  <Typography color="text.secondary">
                    No deliveries match the selected filters.
                  </Typography>
                </Card>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {filteredSalesDeliveries.map((delivery, index) => {
                    const deliveryKey =
                      delivery.id || delivery.localId || `delivery-${index}`;
                    const isExpanded = !!salesOrderExpanded[deliveryKey];

                    return (
                      <Card
                        key={deliveryKey}
                        sx={{ p: 2 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            mb: 1,
                          }}
                        >
                          <Button
                            size="small"
                            onClick={() =>
                              toggleSalesDeliveryExpanded(deliveryKey)
                            }
                          >
                            {isExpanded ? "Collapse" : "Expand"}
                          </Button>
                        </Box>

                        <Typography
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          Rep: {delivery.repName || delivery.userId || "-"} |
                          Hospital:{" "}
                          {delivery.locationName || delivery.locationId || "-"}{" "}
                          | Status: {delivery.status || "-"} | Delivery Date:{" "}
                          {delivery.deliveryDate
                            ? new Date(
                                delivery.deliveryDate,
                              ).toLocaleDateString()
                            : "-"}{" "}
                          | Items: {delivery.deliveryItemCount || 0}
                        </Typography>

                        {isExpanded && (
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>SKU</TableCell>
                                  <TableCell>Inventory Code</TableCell>
                                  <TableCell>Product Code</TableCell>
                                  <TableCell>Lot Number</TableCell>
                                  <TableCell>Cases (Effective)</TableCell>
                                  <TableCell>Original</TableCell>
                                  <TableCell>Status</TableCell>
                                  <TableCell align="right">Action</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {!Array.isArray(delivery.filteredItems) ||
                                delivery.filteredItems.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={8}>
                                      No line items for this delivery.
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  delivery.filteredItems.map((item, idx) => (
                                    <TableRow
                                      key={`${deliveryKey}-${item.skuId || "sku"}-${item.lotNumber || "lot"}-${idx}`}
                                    >
                                      <TableCell>
                                        {item.skuName || item.skuId || "-"}
                                      </TableCell>
                                      <TableCell>
                                        {item.inventoryCode ||
                                          item.clientCode ||
                                          "-"}
                                      </TableCell>
                                      <TableCell>
                                        {item.productCode || item.upc || "-"}
                                      </TableCell>
                                      <TableCell>
                                        {item.lotNumber || "-"}
                                      </TableCell>
                                      <TableCell>
                                        {Number(
                                          item?.effectiveQtyCases ??
                                            item?.enteredQtyCases ??
                                            item?.enteredQty ??
                                            item?.quantity ??
                                            0,
                                        ) || 0}
                                      </TableCell>
                                      <TableCell>
                                        {Number(item?.enteredQtyCases ?? 0) ||
                                          0}
                                      </TableCell>
                                      <TableCell>
                                        {item?.isAdjusted
                                          ? "Adjusted"
                                          : "Original"}
                                      </TableCell>
                                      <TableCell align="right">
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          onClick={() =>
                                            openAdjustmentModal(delivery, item)
                                          }
                                          disabled={!item?.lineId}
                                        >
                                          Adjustment
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  ))
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </Card>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}

          {currentTab === 3 && (
            <Box>
              <Typography
                variant="h6"
                sx={{ mb: 2 }}
              >
                Hospitals & Departments
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                Project location rows available for hospital-facing workflows.
              </Typography>

              {tabLoading ? (
                <Typography>Loading...</Typography>
              ) : (
                <TableContainer component={Card}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Location</TableCell>
                        <TableCell>City</TableCell>
                        <TableCell>Province</TableCell>
                        <TableCell>Banner</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {hospitalRows.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            {row.locationName || row.name || "-"}
                          </TableCell>
                          <TableCell>
                            {row.city || row.address?.city || "-"}
                          </TableCell>
                          <TableCell>
                            {row.province || row.address?.province || "-"}
                          </TableCell>
                          <TableCell>{row.bannerName || "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {currentTab === 4 && (
            <Box>
              <Typography
                variant="h6"
                sx={{ mb: 2 }}
              >
                Positive Snapshot by Locker
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                Official Positive snapshot rows grouped by locker with SKU, lot,
                expiry, and official quantity.
              </Typography>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={positiveSnapshotIncludeHistory}
                      onChange={(event) => {
                        const nextEnabled = event.target.checked;
                        setPositiveSnapshotIncludeHistory(nextEnabled);
                        setPositiveSnapshotStatus("");
                        if (!nextEnabled) {
                          setPositiveSnapshotHistoryDateFrom("");
                          setPositiveSnapshotHistoryDateTo("");
                        }
                      }}
                    />
                  }
                  label="Include history"
                />

                {positiveSnapshotIncludeHistory && (
                  <TextField
                    size="small"
                    type="date"
                    label="Date From"
                    value={positiveSnapshotHistoryDateFrom}
                    onChange={(event) => {
                      setPositiveSnapshotHistoryDateFrom(event.target.value);
                      setPositiveSnapshotStatus("");
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 180 }}
                  />
                )}

                {positiveSnapshotIncludeHistory && (
                  <TextField
                    size="small"
                    type="date"
                    label="Date To"
                    value={positiveSnapshotHistoryDateTo}
                    onChange={(event) => {
                      setPositiveSnapshotHistoryDateTo(event.target.value);
                      setPositiveSnapshotStatus("");
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 180 }}
                  />
                )}

                <FormControl
                  size="small"
                  sx={{ minWidth: 240 }}
                >
                  <InputLabel id="positive-snapshot-sku-filter-label">
                    SKU
                  </InputLabel>
                  <Select
                    labelId="positive-snapshot-sku-filter-label"
                    value={positiveSnapshotSkuFilter}
                    label="SKU"
                    onChange={(event) =>
                      setPositiveSnapshotSkuFilter(event.target.value)
                    }
                  >
                    <MenuItem value="">All SKUs</MenuItem>
                    {positiveSnapshotSkuOptions.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  label="Inventory Code"
                  value={positiveSnapshotInventoryCodeFilter}
                  onChange={(event) =>
                    setPositiveSnapshotInventoryCodeFilter(event.target.value)
                  }
                  sx={{ minWidth: 220 }}
                />

                <TextField
                  size="small"
                  label="Product Code"
                  value={positiveSnapshotProductCodeFilter}
                  onChange={(event) =>
                    setPositiveSnapshotProductCodeFilter(event.target.value)
                  }
                  sx={{ minWidth: 220 }}
                />

                <Button
                  variant="contained"
                  onClick={() => loadTabData(4)}
                  disabled={
                    tabLoading ||
                    (positiveSnapshotIncludeHistory &&
                      (!positiveSnapshotHistoryDateFrom ||
                        !positiveSnapshotHistoryDateTo))
                  }
                >
                  Apply
                </Button>

                <Button
                  variant="outlined"
                  onClick={exportPositiveSnapshotCsv}
                >
                  Export CSV
                </Button>
              </Box>

              {positiveSnapshotStatus ? (
                <Typography
                  sx={{ mb: 2 }}
                  color="error"
                >
                  {positiveSnapshotStatus}
                </Typography>
              ) : null}

              {tabLoading ? (
                <Typography>Loading...</Typography>
              ) : filteredPositiveSnapshotLockers.length === 0 ? (
                <Card sx={{ p: 2 }}>
                  <Typography color="text.secondary">
                    No Positive snapshot rows match the selected filters.
                  </Typography>
                </Card>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {filteredPositiveSnapshotLockers.map((locker, index) => {
                    const lockerKey =
                      locker.id || locker.userId || `locker-${index}`;
                    const isExpanded = !!positiveSnapshotExpanded[lockerKey];

                    return (
                      <Card
                        key={lockerKey}
                        sx={{ p: 2 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700 }}>
                            {locker.name || "Locker"}
                          </Typography>
                          <Button
                            size="small"
                            onClick={() =>
                              togglePositiveSnapshotLocker(lockerKey)
                            }
                          >
                            {isExpanded ? "Collapse" : "Expand"}
                          </Button>
                        </Box>
                        <Typography
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          Rep: {locker.assignedRepName || "Unassigned"} | Rows:{" "}
                          {locker.snapshotRowCount || 0} | Total Official Qty:{" "}
                          {locker.totalOfficialQuantity || 0} | As Of:{" "}
                          {locker.snapshotAsOf
                            ? new Date(locker.snapshotAsOf).toLocaleString()
                            : "-"}
                        </Typography>

                        {isExpanded && (
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>SKU</TableCell>
                                  <TableCell>SAP#</TableCell>
                                  <TableCell>Product Code</TableCell>
                                  <TableCell>Inventory Code</TableCell>
                                  <TableCell>Lot Number</TableCell>
                                  <TableCell>Expiry</TableCell>
                                  <TableCell>Official Qty</TableCell>
                                  <TableCell>Snapshot At</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {!Array.isArray(locker.snapshotItems) ||
                                locker.snapshotItems.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={8}>
                                      No Positive snapshot rows for this locker.
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  locker.snapshotItems.map((item, idx) => (
                                    <TableRow
                                      key={`${lockerKey}-${item.skuId || "sku"}-${item.lotNumber || "lot"}-${item.snapshotAt || "snapshot"}-${idx}`}
                                    >
                                      <TableCell>
                                        {item.skuName || "-"}
                                      </TableCell>
                                      <TableCell>
                                        {item.sapNumber || "-"}
                                      </TableCell>
                                      <TableCell>
                                        {item.productCode || item.skuUpc || "-"}
                                      </TableCell>
                                      <TableCell>
                                        {item.inventoryCode || "-"}
                                      </TableCell>
                                      <TableCell>
                                        {item.lotNumber || "-"}
                                      </TableCell>
                                      <TableCell>
                                        {item.expiryDate
                                          ? new Date(
                                              item.expiryDate,
                                            ).toLocaleDateString()
                                          : "-"}
                                      </TableCell>
                                      <TableCell>
                                        {item.quantityOfficial || 0}
                                      </TableCell>
                                      <TableCell>
                                        {item.snapshotAt
                                          ? new Date(
                                              item.snapshotAt,
                                            ).toLocaleString()
                                          : "-"}
                                      </TableCell>
                                    </TableRow>
                                  ))
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </Card>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}

          {currentTab === 5 && (
            <Box>
              <Typography
                variant="h6"
                sx={{ mb: 2 }}
              >
                Returns
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                Returns grouped by rep and locker, then by shipment. Shipment
                groups include both hospital and locker return rows.
              </Typography>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
                <TextField
                  size="small"
                  type="date"
                  label="Date From"
                  value={returnsDateFrom}
                  onChange={(event) => setReturnsDateFrom(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 180 }}
                />

                <TextField
                  size="small"
                  type="date"
                  label="Date To"
                  value={returnsDateTo}
                  onChange={(event) => setReturnsDateTo(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 180 }}
                />

                <FormControl
                  size="small"
                  sx={{ minWidth: 220 }}
                >
                  <InputLabel id="returns-rep-filter-label">Rep</InputLabel>
                  <Select
                    labelId="returns-rep-filter-label"
                    value={returnsRepFilter}
                    label="Rep"
                    onChange={(event) =>
                      setReturnsRepFilter(event.target.value)
                    }
                  >
                    <MenuItem value="">All Reps</MenuItem>
                    {returnRepOptions.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{ minWidth: 260 }}
                >
                  <InputLabel id="returns-hospital-filter-label">
                    Hospital
                  </InputLabel>
                  <Select
                    labelId="returns-hospital-filter-label"
                    value={returnsHospitalFilter}
                    label="Hospital"
                    onChange={(event) =>
                      setReturnsHospitalFilter(event.target.value)
                    }
                  >
                    <MenuItem value="">All Hospitals</MenuItem>
                    {returnHospitalOptions.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{ minWidth: 260 }}
                >
                  <InputLabel id="returns-sku-filter-label">SKU</InputLabel>
                  <Select
                    labelId="returns-sku-filter-label"
                    value={returnsSkuFilter}
                    label="SKU"
                    onChange={(event) =>
                      setReturnsSkuFilter(event.target.value)
                    }
                  >
                    <MenuItem value="">All SKUs</MenuItem>
                    {returnSkuOptions.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  label="Lot Number"
                  value={returnsLotNumberFilter}
                  onChange={(event) =>
                    setReturnsLotNumberFilter(event.target.value)
                  }
                  sx={{ minWidth: 220 }}
                />

                <FormControl
                  size="small"
                  sx={{ minWidth: 180 }}
                >
                  <InputLabel id="returns-type-filter-label">Type</InputLabel>
                  <Select
                    labelId="returns-type-filter-label"
                    value={returnsTypeFilter}
                    label="Type"
                    onChange={(event) =>
                      setReturnsTypeFilter(event.target.value)
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="hospital">Hospital</MenuItem>
                    <MenuItem value="locker">Locker</MenuItem>
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{ minWidth: 180 }}
                >
                  <InputLabel id="returns-processed-filter-label">
                    Processed
                  </InputLabel>
                  <Select
                    labelId="returns-processed-filter-label"
                    value={returnsProcessedFilter}
                    label="Processed"
                    onChange={(event) =>
                      setReturnsProcessedFilter(event.target.value)
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Draft">Draft</MenuItem>
                    <MenuItem value="Pickup Requested">
                      Pickup Requested
                    </MenuItem>
                    <MenuItem value="Picked Up">Picked Up</MenuItem>
                    <MenuItem value="Closed">Closed</MenuItem>
                    <MenuItem value="Processed">Processed</MenuItem>
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{ minWidth: 180 }}
                >
                  <InputLabel id="returns-emailed-filter-label">
                    Emailed
                  </InputLabel>
                  <Select
                    labelId="returns-emailed-filter-label"
                    value={returnsEmailedFilter}
                    label="Emailed"
                    onChange={(event) =>
                      setReturnsEmailedFilter(event.target.value)
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Not Eligible">Not Eligible</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Sent">Sent</MenuItem>
                    <MenuItem value="Failed">Failed</MenuItem>
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  onClick={runReturnsQuery}
                  disabled={tabLoading}
                >
                  Execute
                </Button>

                <Button
                  variant="outlined"
                  onClick={clearReturnsFilters}
                  disabled={tabLoading}
                >
                  Clear
                </Button>

                <Button
                  variant="outlined"
                  onClick={exportReturnsCsv}
                  disabled={
                    !returnsHasRun || filteredReturnTransactions.length === 0
                  }
                >
                  Export CSV
                </Button>
              </Box>

              {returnsHasRun && returnsQueryStatus ? (
                <Typography
                  sx={{ mb: 2 }}
                  color="text.secondary"
                >
                  {returnsQueryStatus}
                </Typography>
              ) : null}

              {returnsSlipStatus ? (
                <Typography
                  sx={{ mb: 2 }}
                  color="text.secondary"
                >
                  {returnsSlipStatus}
                </Typography>
              ) : null}

              {tabLoading ? (
                <Typography>Loading...</Typography>
              ) : !returnsHasRun ? (
                <Card sx={{ p: 2 }}>
                  <Typography color="text.secondary">
                    {returnsQueryStatus ||
                      "Set at least one filter and click Execute."}
                  </Typography>
                </Card>
              ) : filteredReturnTransactions.length === 0 ? (
                <Card sx={{ p: 2 }}>
                  <Typography color="text.secondary">
                    No return transactions match the selected filters.
                  </Typography>
                </Card>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {groupedReturnTransactions.map((majorGroup) => {
                    const majorExpanded =
                      !!returnsMajorExpanded[majorGroup.key];

                    return (
                      <Card
                        key={majorGroup.key}
                        sx={{ p: 2 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                          }}
                        >
                          <Box>
                            <Typography sx={{ fontWeight: 700 }}>
                              {majorGroup.rep.label}
                            </Typography>
                            <Typography
                              color="text.secondary"
                              sx={{ fontSize: 14 }}
                            >
                              Locker: {majorGroup.locker.label} | Returns:{" "}
                              {majorGroup.totalRows} | Shipments:{" "}
                              {majorGroup.shipments.length}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() =>
                              toggleReturnsMajorGroup(majorGroup.key)
                            }
                          >
                            {majorExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </Box>

                        <Collapse in={majorExpanded}>
                          <Box
                            sx={{
                              mt: 2,
                              display: "flex",
                              flexDirection: "column",
                              gap: 1.5,
                            }}
                          >
                            {majorGroup.shipments.map((shipmentGroup) => {
                              const shipmentExpanded =
                                !!returnsShipmentExpanded[
                                  `${majorGroup.key}::${shipmentGroup.key}`
                                ];

                              const shipmentLabel = shipmentGroup.isProcessed
                                ? `Shipment ${shipmentGroup.shipmentId}`
                                : "Not Processed";

                              const shipmentDateLabel =
                                shipmentGroup.isProcessed
                                  ? shipmentGroup.maxProcessedAt > 0
                                    ? new Date(
                                        shipmentGroup.maxProcessedAt,
                                      ).toLocaleString()
                                    : "Date unavailable"
                                  : "";

                              return (
                                <Card
                                  key={`${majorGroup.key}-${shipmentGroup.key}`}
                                  variant="outlined"
                                  sx={{ p: 1.5 }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 1,
                                    }}
                                  >
                                    <Box>
                                      <Typography sx={{ fontWeight: 600 }}>
                                        {shipmentLabel}
                                      </Typography>
                                      <Typography
                                        color="text.secondary"
                                        sx={{ fontSize: 13 }}
                                      >
                                        {shipmentGroup.isProcessed
                                          ? `Processed: ${shipmentDateLabel} | Returns: ${shipmentGroup.rows.length}`
                                          : `Returns: ${shipmentGroup.rows.length}`}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() =>
                                          recreateShipmentPackingSlip({
                                            shipmentId:
                                              shipmentGroup.shipmentId || "",
                                            rows: shipmentGroup.rows,
                                            label: shipmentLabel,
                                          })
                                        }
                                      >
                                        {shipmentGroup.isProcessed
                                          ? "Recreate Packing Slip"
                                          : "Preview Packing Slip"}
                                      </Button>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          toggleReturnsShipmentGroup(
                                            majorGroup.key,
                                            shipmentGroup.key,
                                          )
                                        }
                                      >
                                        {shipmentExpanded ? (
                                          <ExpandLess />
                                        ) : (
                                          <ExpandMore />
                                        )}
                                      </IconButton>
                                    </Box>
                                  </Box>

                                  <Collapse in={shipmentExpanded}>
                                    <TableContainer sx={{ mt: 1 }}>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Return Date</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Location</TableCell>
                                            <TableCell>SKU</TableCell>
                                            <TableCell>Product Code</TableCell>
                                            <TableCell>Lot Number</TableCell>
                                            <TableCell>Expiry Date</TableCell>
                                            <TableCell>Qty</TableCell>
                                            <TableCell>Reason</TableCell>
                                            <TableCell>Processed</TableCell>
                                            <TableCell>Emailed</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {shipmentGroup.rows.map(
                                            (row, index) => {
                                              const processingChip =
                                                getReturnProcessingChipProps(
                                                  row?.returnProcessingStatus,
                                                );
                                              const emailChip =
                                                getReturnEmailChipProps(
                                                  row?.returnShipmentEmailStatus,
                                                );
                                              const processedAtText =
                                                row?.returnHeaderProcessedAt
                                                  ? new Date(
                                                      row.returnHeaderProcessedAt,
                                                    ).toLocaleString()
                                                  : "Not processed";
                                              const shipmentIdText =
                                                row?.returnHeaderProcessedShipmentId ||
                                                "No shipment ID";
                                              const emailTooltip = [
                                                `Status: ${emailChip.label}`,
                                                `Attempts: ${Number(
                                                  row?.returnShipmentEmailAttemptCount ||
                                                    0,
                                                )}`,
                                                `Sent At: ${
                                                  row?.returnShipmentEmailSentAt
                                                    ? new Date(
                                                        row.returnShipmentEmailSentAt,
                                                      ).toLocaleString()
                                                    : "-"
                                                }`,
                                                `Last Error: ${
                                                  row?.returnShipmentEmailLastError ||
                                                  "-"
                                                }`,
                                              ].join("\n");

                                              return (
                                                <TableRow
                                                  key={
                                                    row?.id ||
                                                    row?.clientTransactionId ||
                                                    `${majorGroup.key}-${shipmentGroup.key}-row-${index}`
                                                  }
                                                >
                                                  <TableCell>
                                                    {row?.transactionDate
                                                      ? new Date(
                                                          row.transactionDate,
                                                        ).toLocaleDateString()
                                                      : "-"}
                                                  </TableCell>
                                                  <TableCell>
                                                    {getReturnTypeLabel(
                                                      row?.returnType,
                                                    )}
                                                  </TableCell>
                                                  <TableCell>
                                                    {row?.returnLocation
                                                      ?.name ||
                                                      row?.hospitalLocation
                                                        ?.name ||
                                                      "-"}
                                                  </TableCell>
                                                  <TableCell>
                                                    {row?.sku?.name ||
                                                      row?.skuId ||
                                                      "-"}
                                                  </TableCell>
                                                  <TableCell>
                                                    {row?.sku?.code || "-"}
                                                  </TableCell>
                                                  <TableCell>
                                                    {row?.lotNumber || "-"}
                                                  </TableCell>
                                                  <TableCell>
                                                    {row?.expiryDate
                                                      ? new Date(
                                                          row.expiryDate,
                                                        ).toLocaleDateString()
                                                      : "-"}
                                                  </TableCell>
                                                  <TableCell>
                                                    {Number(
                                                      row?.quantity || 0,
                                                    ) || 0}
                                                  </TableCell>
                                                  <TableCell>
                                                    {row?.reason || "-"}
                                                  </TableCell>
                                                  <TableCell>
                                                    <Tooltip
                                                      title={`Status: ${processingChip.label}\nProcessed At: ${processedAtText}\nShipment ID: ${shipmentIdText}`}
                                                      arrow
                                                    >
                                                      <Chip
                                                        size="small"
                                                        color={
                                                          processingChip.color
                                                        }
                                                        variant={
                                                          processingChip.variant
                                                        }
                                                        label={
                                                          processingChip.label
                                                        }
                                                      />
                                                    </Tooltip>
                                                  </TableCell>
                                                  <TableCell>
                                                    <Tooltip
                                                      title={emailTooltip}
                                                      arrow
                                                    >
                                                      <Chip
                                                        size="small"
                                                        color={emailChip.color}
                                                        variant={
                                                          emailChip.variant
                                                        }
                                                        label={emailChip.label}
                                                      />
                                                    </Tooltip>
                                                  </TableCell>
                                                </TableRow>
                                              );
                                            },
                                          )}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </Collapse>
                                </Card>
                              );
                            })}
                          </Box>
                        </Collapse>
                      </Card>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}

          {currentTab === 6 && (
            <Box>
              <Typography
                variant="h6"
                sx={{ mb: 2 }}
              >
                Reports
              </Typography>

              <Card sx={{ p: 2, mb: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, mb: 1 }}
                >
                  Report Filters
                </Typography>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  <FormControl
                    size="small"
                    sx={{ minWidth: 240 }}
                  >
                    <InputLabel id="report-type-filter-label">
                      Report Type
                    </InputLabel>
                    <Select
                      labelId="report-type-filter-label"
                      value={selectedReportType}
                      label="Report Type"
                      onChange={(event) =>
                        setSelectedReportType(event.target.value)
                      }
                    >
                      <MenuItem value="consumption">
                        Consumption Report
                      </MenuItem>
                      <MenuItem value="lotExpiry">
                        Lot and Expiry Report
                      </MenuItem>
                      <MenuItem value="hospitalLocationSummary">
                        Hospital Location Summary
                      </MenuItem>
                      <MenuItem value="hospitalLocationDetail">
                        Hospital Location Detail
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    size="small"
                    type="date"
                    label="Date From"
                    value={consumptionDateFrom}
                    onChange={(event) =>
                      setConsumptionDateFrom(event.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 180 }}
                    required
                  />

                  <TextField
                    size="small"
                    type="date"
                    label="Date To"
                    value={consumptionDateTo}
                    onChange={(event) =>
                      setConsumptionDateTo(event.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 180 }}
                    required
                  />

                  <FormControl
                    size="small"
                    sx={{ minWidth: 260 }}
                  >
                    <InputLabel id="consumption-hospital-filter-label">
                      Location
                    </InputLabel>
                    <Select
                      labelId="consumption-hospital-filter-label"
                      value={consumptionHospitalCodeFilter}
                      label="Location"
                      onChange={(event) =>
                        setConsumptionHospitalCodeFilter(event.target.value)
                      }
                    >
                      <MenuItem value="">All Locations</MenuItem>
                      {consumptionHospitalOptions.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedReportType === "lotExpiry" && (
                    <TextField
                      size="small"
                      label="Inventory Code"
                      value={lotExpiryInventoryCodeFilter}
                      onChange={(event) =>
                        setLotExpiryInventoryCodeFilter(event.target.value)
                      }
                      sx={{ minWidth: 220 }}
                    />
                  )}

                  <FormControl
                    size="small"
                    sx={{ minWidth: 220 }}
                  >
                    <InputLabel id="consumption-rep-filter-label">
                      Rep
                    </InputLabel>
                    <Select
                      labelId="consumption-rep-filter-label"
                      value={consumptionRepFilter}
                      label="Rep"
                      onChange={(event) =>
                        setConsumptionRepFilter(event.target.value)
                      }
                    >
                      <MenuItem value="">All Reps</MenuItem>
                      {consumptionRepOptions.map((repName) => (
                        <MenuItem
                          key={repName}
                          value={repName}
                        >
                          {repName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Button
                    variant="contained"
                    onClick={runSelectedReport}
                    disabled={isRunDisabled}
                  >
                    Run {getSelectedReportLabel()}
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={exportSelectedReport}
                    disabled={isExportDisabled}
                  >
                    Export {getSelectedReportLabel()} CSV
                  </Button>
                </Box>
              </Card>

              <Card sx={{ p: 2, mb: 2 }}>
                <Typography
                  variant="h6"
                  sx={{ mb: 1 }}
                >
                  {getSelectedReportLabel()}
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {selectedReportType === "lotExpiry"
                    ? "Delivered units by inventory code with lot and expiry details."
                    : selectedReportType === "hospitalLocationSummary"
                      ? "Delivered units summarized by rep, hospital, hospital location, and product."
                      : selectedReportType === "hospitalLocationDetail"
                        ? "Delivered units detailed by rep, hospital, hospital location, product, and delivery date."
                        : "Delivered units summarized at product level by location."}
                </Typography>

                {!hasConsumptionDateRangeInput ? (
                  <Typography color="text.secondary">
                    Date From and Date To are required to run this report.
                  </Typography>
                ) : !selectedReportHasRun ? (
                  <Typography color="text.secondary">
                    Set filters and click Run {getSelectedReportLabel()}.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {selectedReportType === "lotExpiry" && (
                            <TableCell>Reference Number</TableCell>
                          )}
                          <TableCell>Rep Name</TableCell>
                          <TableCell>Hospital Code</TableCell>
                          <TableCell>Hospital Name</TableCell>
                          {selectedReportType === "consumption" && (
                            <>
                              <TableCell>Street Address</TableCell>
                              <TableCell>City</TableCell>
                              <TableCell>Province</TableCell>
                              <TableCell>Postal</TableCell>
                            </>
                          )}
                          {selectedReportType === "lotExpiry" && (
                            <TableCell>Delivery Date</TableCell>
                          )}
                          {selectedReportType === "hospitalLocationDetail" && (
                            <>
                              <TableCell>Delivery Date</TableCell>
                              <TableCell>Product Code</TableCell>
                              <TableCell>SKU Name</TableCell>
                              <TableCell>Hospital Location</TableCell>
                            </>
                          )}
                          {selectedReportType === "hospitalLocationSummary" && (
                            <>
                              <TableCell>Product Code</TableCell>
                              <TableCell>SKU Name</TableCell>
                              <TableCell>Hospital Location</TableCell>
                            </>
                          )}
                          {(selectedReportType === "consumption" ||
                            selectedReportType === "lotExpiry") && (
                            <TableCell>Product Code</TableCell>
                          )}
                          {(selectedReportType === "consumption" ||
                            selectedReportType === "lotExpiry") && (
                            <TableCell>SKU Name</TableCell>
                          )}
                          {selectedReportType === "lotExpiry" && (
                            <>
                              <TableCell>Inventory Code</TableCell>
                              <TableCell>Lot Number</TableCell>
                              <TableCell>Expiry Date</TableCell>
                            </>
                          )}
                          <TableCell>Delivered Units</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedReportRows.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={
                                selectedReportType === "lotExpiry"
                                  ? 11
                                  : selectedReportType ===
                                      "hospitalLocationSummary"
                                    ? 7
                                    : selectedReportType ===
                                        "hospitalLocationDetail"
                                      ? 8
                                      : 10
                              }
                            >
                              No report data matches the selected filters.
                            </TableCell>
                          </TableRow>
                        ) : (
                          pagedConsumptionRows.map((row, idx) => (
                            <TableRow
                              key={`${row.hospitalCode}-${row.hospitalName || row.locationName}-${row.hospitalLocationName || ""}-${row.repName}-${row.productCode}-${row.deliveryDate || ""}-${row.referenceNumber || ""}-${idx}`}
                            >
                              {selectedReportType === "lotExpiry" && (
                                <TableCell>
                                  {row.referenceNumber || "-"}
                                </TableCell>
                              )}
                              <TableCell>{row.repName}</TableCell>
                              <TableCell>{row.hospitalCode}</TableCell>
                              <TableCell>
                                {row.hospitalName || row.locationName}
                              </TableCell>
                              {selectedReportType === "consumption" && (
                                <>
                                  <TableCell>{row.streetAddress}</TableCell>
                                  <TableCell>{row.city}</TableCell>
                                  <TableCell>{row.province}</TableCell>
                                  <TableCell>{row.postal}</TableCell>
                                </>
                              )}
                              {selectedReportType === "lotExpiry" && (
                                <TableCell>{row.deliveryDate}</TableCell>
                              )}
                              {selectedReportType ===
                                "hospitalLocationDetail" && (
                                <>
                                  <TableCell>{row.deliveryDate}</TableCell>
                                  <TableCell>{row.productCode}</TableCell>
                                  <TableCell>{row.skuName}</TableCell>
                                  <TableCell>
                                    {row.hospitalLocationName}
                                  </TableCell>
                                </>
                              )}
                              {selectedReportType ===
                                "hospitalLocationSummary" && (
                                <>
                                  <TableCell>{row.productCode}</TableCell>
                                  <TableCell>{row.skuName}</TableCell>
                                  <TableCell>
                                    {row.hospitalLocationName}
                                  </TableCell>
                                </>
                              )}
                              {(selectedReportType === "consumption" ||
                                selectedReportType === "lotExpiry") && (
                                <>
                                  <TableCell>{row.productCode}</TableCell>
                                  <TableCell>{row.skuName}</TableCell>
                                </>
                              )}
                              {selectedReportType === "lotExpiry" && (
                                <>
                                  <TableCell>{row.inventoryCode}</TableCell>
                                  <TableCell>{row.lotNumber}</TableCell>
                                  <TableCell>{row.expiryDate}</TableCell>
                                </>
                              )}
                              <TableCell>{row.deliveredUnits}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                    <TablePagination
                      component="div"
                      count={selectedReportRows.length}
                      page={consumptionPage}
                      onPageChange={(event, nextPage) =>
                        setConsumptionPage(nextPage)
                      }
                      rowsPerPage={consumptionRowsPerPage}
                      onRowsPerPageChange={(event) => {
                        const nextValue = Number(event.target.value || 25);
                        setConsumptionRowsPerPage(nextValue);
                      }}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                  </TableContainer>
                )}
              </Card>
            </Box>
          )}

          {currentTab === 7 && isSysAdmin && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography
                variant="h6"
                sx={{ mb: 1 }}
              >
                Positive API Settings
              </Typography>
              <Typography color="text.secondary">
                Sysadmin-only credentials, token, and schedule controls for
                daily inventory sync.
              </Typography>

              <Card sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 700, mb: 2 }}>
                  Credentials
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  <TextField
                    size="small"
                    label="Base URL"
                    value={positiveSettings.credentials.baseUrl || ""}
                    onChange={(event) =>
                      setPositiveSettings((current) => ({
                        ...current,
                        credentials: {
                          ...current.credentials,
                          baseUrl: event.target.value,
                        },
                      }))
                    }
                    sx={{ minWidth: 280 }}
                  />
                  <TextField
                    size="small"
                    label="Token URL"
                    value={positiveSettings.credentials.tokenUrl || ""}
                    onChange={(event) =>
                      setPositiveSettings((current) => ({
                        ...current,
                        credentials: {
                          ...current.credentials,
                          tokenUrl: event.target.value,
                        },
                      }))
                    }
                    sx={{ minWidth: 320 }}
                  />
                  <TextField
                    size="small"
                    label="Inventory Path"
                    value={positiveSettings.credentials.inventoryPath || ""}
                    onChange={(event) =>
                      setPositiveSettings((current) => ({
                        ...current,
                        credentials: {
                          ...current.credentials,
                          inventoryPath: event.target.value,
                        },
                      }))
                    }
                    sx={{ minWidth: 240 }}
                  />
                  <TextField
                    size="small"
                    label="Products Path"
                    value={positiveSettings.credentials.productsPath || ""}
                    onChange={(event) =>
                      setPositiveSettings((current) => ({
                        ...current,
                        credentials: {
                          ...current.credentials,
                          productsPath: event.target.value,
                        },
                      }))
                    }
                    sx={{ minWidth: 240 }}
                  />
                  <TextField
                    size="small"
                    label="Audience"
                    value={positiveSettings.credentials.audience || ""}
                    onChange={(event) =>
                      setPositiveSettings((current) => ({
                        ...current,
                        credentials: {
                          ...current.credentials,
                          audience: event.target.value,
                        },
                      }))
                    }
                    sx={{ minWidth: 280 }}
                  />
                  <TextField
                    size="small"
                    label="Scope"
                    value={positiveSettings.credentials.scope || ""}
                    onChange={(event) =>
                      setPositiveSettings((current) => ({
                        ...current,
                        credentials: {
                          ...current.credentials,
                          scope: event.target.value,
                        },
                      }))
                    }
                    sx={{ minWidth: 240 }}
                  />
                  <TextField
                    size="small"
                    label="Customer Code"
                    value={positiveSettings.credentials.customerCode || ""}
                    onChange={(event) =>
                      setPositiveSettings((current) => ({
                        ...current,
                        credentials: {
                          ...current.credentials,
                          customerCode: event.target.value,
                        },
                      }))
                    }
                    sx={{ minWidth: 220 }}
                  />
                  <TextField
                    size="small"
                    label="API Client ID"
                    value={positiveSettings.credentials.clientId || ""}
                    onChange={(event) =>
                      setPositiveSettings((current) => ({
                        ...current,
                        credentials: {
                          ...current.credentials,
                          clientId: event.target.value,
                        },
                      }))
                    }
                    sx={{ minWidth: 220 }}
                  />
                  <TextField
                    size="small"
                    type="password"
                    label="API Client Secret"
                    value={positiveSettings.credentials.clientSecret || ""}
                    onChange={(event) =>
                      setPositiveSettings((current) => ({
                        ...current,
                        credentials: {
                          ...current.credentials,
                          clientSecret: event.target.value,
                        },
                      }))
                    }
                    sx={{ minWidth: 240 }}
                  />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={savePositiveCredentials}
                  >
                    Save Credentials
                  </Button>
                </Box>
              </Card>

              <Card sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 700, mb: 2 }}>
                  Schedule
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  <TextField
                    size="small"
                    label="Timezone"
                    value={positiveSettings.schedule.timezone || ""}
                    onChange={(event) =>
                      setPositiveSettings((current) => ({
                        ...current,
                        schedule: {
                          ...current.schedule,
                          timezone: event.target.value,
                        },
                      }))
                    }
                    sx={{ minWidth: 220 }}
                  />
                  <TextField
                    size="small"
                    type="time"
                    label="Run Time"
                    value={positiveSettings.schedule.runTime || "06:00"}
                    onChange={(event) =>
                      setPositiveSettings((current) => ({
                        ...current,
                        schedule: {
                          ...current.schedule,
                          runTime: event.target.value,
                        },
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 180 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Refresh Buffer Minutes"
                    value={positiveSettings.schedule.refreshBufferMinutes || 15}
                    onChange={(event) =>
                      setPositiveSettings((current) => ({
                        ...current,
                        schedule: {
                          ...current.schedule,
                          refreshBufferMinutes:
                            Number(event.target.value || 15) || 15,
                        },
                      }))
                    }
                    sx={{ minWidth: 220 }}
                  />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={savePositiveSchedule}
                  >
                    Save Schedule
                  </Button>
                </Box>
              </Card>

              <Card sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 700, mb: 2 }}>Token</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  <TextField
                    size="small"
                    label="Access Token"
                    value={positiveSettings.token.accessToken || ""}
                    onChange={(event) =>
                      setPositiveSettings((current) => ({
                        ...current,
                        token: {
                          ...current.token,
                          accessToken: event.target.value,
                        },
                      }))
                    }
                    sx={{ minWidth: 360, flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Token Type"
                    value={positiveSettings.token.tokenType || "Bearer"}
                    onChange={(event) =>
                      setPositiveSettings((current) => ({
                        ...current,
                        token: {
                          ...current.token,
                          tokenType: event.target.value,
                        },
                      }))
                    }
                    sx={{ minWidth: 180 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Expires In Seconds"
                    value={positiveSettings.token.expiresInSeconds || 0}
                    onChange={(event) =>
                      setPositiveSettings((current) => ({
                        ...current,
                        token: {
                          ...current.token,
                          expiresInSeconds: Number(event.target.value || 0),
                        },
                      }))
                    }
                    sx={{ minWidth: 200 }}
                  />
                  <TextField
                    size="small"
                    type="datetime-local"
                    label="Expires At (UTC)"
                    value={
                      positiveSettings.token.expiresAtUtc
                        ? new Date(positiveSettings.token.expiresAtUtc)
                            .toISOString()
                            .slice(0, 16)
                        : ""
                    }
                    onChange={(event) =>
                      setPositiveSettings((current) => ({
                        ...current,
                        token: {
                          ...current.token,
                          expiresAtUtc: event.target.value
                            ? new Date(event.target.value).toISOString()
                            : "",
                        },
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 240 }}
                  />
                </Box>
                <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={savePositiveToken}
                  >
                    Save Token
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={refreshPositiveToken}
                  >
                    Refresh Token Now
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={runPositiveSyncNow}
                  >
                    Run Sync Now
                  </Button>
                </Box>
              </Card>

              <Card sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>
                  Latest Run
                </Typography>
                {positiveSettings.latestRun ? (
                  <Typography color="text.secondary">
                    Status: {positiveSettings.latestRun.status || "-"} |
                    Started:{" "}
                    {positiveSettings.latestRun.startedAt
                      ? new Date(
                          positiveSettings.latestRun.startedAt,
                        ).toLocaleString()
                      : "-"}{" "}
                    | Completed:{" "}
                    {positiveSettings.latestRun.completedAt
                      ? new Date(
                          positiveSettings.latestRun.completedAt,
                        ).toLocaleString()
                      : "-"}{" "}
                    | Read: {positiveSettings.latestRun.recordsRead || 0} |
                    Accepted: {positiveSettings.latestRun.recordsAccepted || 0}{" "}
                    | Errors: {positiveSettings.latestRun.errorCount || 0}
                  </Typography>
                ) : (
                  <Typography color="text.secondary">
                    No run history yet.
                  </Typography>
                )}
              </Card>

              {positiveSettingsStatus ? (
                <Card sx={{ p: 2 }}>
                  <Typography color="text.secondary">
                    {positiveSettingsStatus}
                  </Typography>
                </Card>
              ) : null}
            </Box>
          )}
        </Box>
      </Box>

      <Dialog_YesNo
        open={!!resendConfirmReceipt}
        onClose={() => setResendConfirmReceipt(null)}
        title="Confirm Receipt Resend"
        message={
          "This should only be done if you are sure this receipt has not been processed.\n\nDo you want to resend this receipt email?"
        }
        onYes={async () => {
          if (!resendConfirmReceipt) return;
          const { orderId, receiptId } = resendConfirmReceipt;
          setResendConfirmReceipt(null);
          await handleManualSendPoReceipt(orderId, receiptId, {
            resend: true,
          });
        }}
        onNo={() => setResendConfirmReceipt(null)}
      />

      <Dialog_Flex
        open={adjustmentModalOpen}
        onClose={closeAdjustmentModal}
        title="Delivery Line Adjustment"
        message={
          selectedDeliveryLine
            ? `SKU: ${selectedDeliveryLine.skuName}\nInventory Code: ${selectedDeliveryLine.inventoryCode}\nProduct Code: ${selectedDeliveryLine.productCode}\nLot: ${selectedDeliveryLine.lotNumber}\nOriginal Cases: ${selectedDeliveryLine.originalQtyCases}`
            : ""
        }
        actions={[
          { name: "cancel", label: "cancel" },
          { name: "save", label: "save" },
        ]}
        onAction={(action) => {
          if (action === "cancel") {
            closeAdjustmentModal();
            return;
          }
          if (action === "save") {
            saveDeliveryAdjustment();
          }
        }}
      >
        <Box sx={{ px: 3, pb: 2, display: "grid", gap: 2 }}>
          <TextField
            label="Adjusted Cases"
            type="number"
            inputProps={{ min: 0, step: 1 }}
            value={adjustedQtyCasesInput}
            onChange={(event) => setAdjustedQtyCasesInput(event.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Reason (optional)"
            value={adjustmentReasonInput}
            onChange={(event) => setAdjustmentReasonInput(event.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Notes (optional)"
            value={adjustmentNotesInput}
            onChange={(event) => setAdjustmentNotesInput(event.target.value)}
            multiline
            minRows={2}
            fullWidth
            size="small"
          />
          {!!adjustmentStatus && (
            <Typography color="text.secondary">{adjustmentStatus}</Typography>
          )}
        </Box>
      </Dialog_Flex>
    </Page>
  );
};

export default Inventory;
